/* CUSTOM-START: JL smart picker v4.12.1. Woodstock remains the only variant renderer. */
(() => {
  'use strict';
  if (customElements.get('jl-smart-picker')) return;
  const extraSessions = new Map();
  const addonSessions = new Map();
  let specialCctVariants;

  class JLSmartPicker extends HTMLElement {
    connectedCallback() {
      // Dynamically inserted sections can connect before their children exist.
      this.startTimer = setTimeout(() => this.start(), 0);
    }

    disconnectedCallback() {
      clearTimeout(this.startTimer);
      clearTimeout(this.finishTimer);
      this.exclusionPurchaseListeners?.abort();
      this.exclusionPurchaseListeners = null;
      this.exclusionRecovery?.abort();
      this.nativeExclusionListeners?.abort();
      this.request?.abort();
      this.listeners?.abort();
      this.purchaseObserver?.disconnect();
      this.propertyInputs?.remove();
      this.closeInfo(false);
      this.restoreAddonCart();
      this.restoreAddon();
      this.restoreNative();
    }

    start() {
      try {
        const dependencies = ['variant-radios', 'product-info'].filter(name => !customElements.get(name));
        if (dependencies.length) {
          Promise.all(dependencies.map(name => customElements.whenDefined(name))).then(() => {
            if (this.isConnected) this.start();
          });
          return;
        }
        this.native = document.getElementById(this.dataset.nativeId);
        this.config = this.readJSON(this, '[data-jl-config]');
        if (this.config.exclude_variants && !this.exclusionPurchaseListeners) {
          // Cover excluded deep links while Woodstock acknowledges corrected controls,
          // including native recovery before the normal purchase guard is installed.
          this.exclusionPurchaseListeners = new AbortController();
          this.purchaseForms().forEach(form => ['submit', 'click', 'keydown'].forEach(type =>
            form.addEventListener(type, event => {
              if (type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
              if (this.config.exclude_variants?.ids?.includes(String(form.querySelector('[name="id"]')?.value))) {
                event.preventDefault(); event.stopImmediatePropagation();
              }
            }, { capture: true, signal: this.exclusionPurchaseListeners.signal })));
        }
        this.validateValueGroups();
        this.validatePresentation();
        if (this.config.addons != null || this.hasSurcharges()) {
          if (!customElements.get('product-form')) {
            customElements.whenDefined('product-form').then(() => { if (this.isConnected) this.start(); });
            return;
          }
          this.addonProducts = this.config.addons ? this.readJSON(this, '[data-jl-addons]') : {};
          if (specialCctVariants) this.addonProducts['special-cct'] = specialCctVariants;
          this.validateAddons();
        }
        this.state = this.exclusionState(this.readJSON(this, '[data-jl-state]'));
        if (this.config.exclude_variants && !this.state.variant) {
          this.exclusionRecovery?.abort();
          this.exclusionRecovery = new AbortController();
          document.addEventListener('afterVariantChanged', event => {
            if (String(event.detail?.sectionId) !== this.dataset.section || !this.isConnected) return;
            // Woodstock keeps this element; take the corrected variant's state from the fetched section.
            const fresh = event.detail.html?.getElementById?.(this.id)?.querySelector?.('[data-jl-state]');
            const own = this.querySelector('[data-jl-state]');
            if (fresh && own) own.textContent = fresh.textContent;
            this.exclusionRecovery.abort(); this.start();
          }, { signal: this.exclusionRecovery.signal });
          this.restoreNative(); this.stopLoading(); return;
        }
        if (!this.native || this.native.tagName !== 'VARIANT-RADIOS') return this.stopLoading();
        if (!this.state.variant || !this.validState(this.state)) return this.stopLoading();
        this.labels = this.config.split_labels || [];
        this.separator = this.config.split_separator || ' – ';
        this.splitIndex = this.state.options.findIndex(o => o.name === this.config.split_option);
        if (this.splitIndex >= 0 && this.labels.length) {
          const wellFormed = this.state.options[this.splitIndex].values.every(v =>
            v.name.split(this.separator).length === this.labels.length);
          if (!wellFormed) throw new Error('Split labels do not match option values');
        } else {
          this.splitIndex = -1;
        }
        this.steps = this.makeSteps();
        this.validateExtras();
        this.productKey = new URL(this.dataset.productUrl, location.origin).pathname;
        if (!extraSessions.has(this.productKey)) extraSessions.set(this.productKey, new Map());
        this.extraSelections = extraSessions.get(this.productKey);
        if (this.config.addons) {
          if (!addonSessions.has(this.productKey)) addonSessions.set(this.productKey, new Map());
          this.addonSelections = addonSessions.get(this.productKey);
        }
        this.syncNativeExtras();
        this.reconcileExtras();
        this.activeStep = this.missingStep()?.name || null;
        this.listeners = new AbortController();
        const signal = this.listeners.signal;
        this.addEventListener('click', e => this.handleClick(e), { signal });
        this.addEventListener('keydown', e => this.handleKey(e), { signal });
        const dismissInfo = e => {
          if (this.infoPopover && !this.infoPopover.contains(e.target) && !this.infoTrigger?.contains(e.target)) this.closeInfo();
        };
        document.addEventListener('pointerdown', dismissInfo, { signal, capture: true });
        document.addEventListener('click', dismissInfo, { signal });
        document.addEventListener('keydown', e => { if (e.key === 'Escape') this.closeInfo(); }, { signal });
        document.addEventListener('afterVariantChanged', e => this.afterVariantChanged(e), { signal });
        document.addEventListener('jlPickerExtrasChanged', e => {
          if (e.detail.productKey === this.productKey && e.detail.source !== this) {
            this.render(); this.syncPurchaseForms();
          }
        }, { signal });
        // Capture before theme/app handlers, including composed payment-button clicks.
        ['submit', 'click', 'keydown'].forEach(type => window.addEventListener(type,
          e => this.guardPurchase(e), { signal, capture: true }));
        this.purchaseObserver = new MutationObserver(() => this.syncPurchaseForms());
        this.purchaseInfos().forEach(info => info.addEventListener('input', () => this.syncPurchaseForms(), { signal }));
        this.purchaseObserver.observe(document.body, { childList: true, subtree: true,
          attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'tabindex', 'disabled'] });
        this.render();
        this.syncPurchaseForms();
        this.hideNative();
        this.hidden = false;
        this.stopLoading();
        void this.loadSurcharges();
      } catch {
        // Bad/unfinished merchant configuration must never remove the real picker.
        this.listeners?.abort();
        this.purchaseObserver?.disconnect();
        this.propertyInputs?.remove();
        this.restoreAddonCart();
        this.restoreAddon();
        this.restoreNative();
        this.hidden = true;
        this.stopLoading();
      }
    }

    // The snippet hides the original picker behind a placeholder until start() settles.
    stopLoading() {
      this.removeAttribute('data-jl-pending');
    }

    readJSON(root, selector) {
      const node = root.querySelector(selector);
      if (!node) throw new Error('Missing picker data');
      return JSON.parse(node.textContent);
    }

    validState(state) {
      return Array.isArray(state?.options) && state.options.length > 0 &&
        state.options.every(o => typeof o.name === 'string' && Array.isArray(o.values) &&
          o.values.length && o.values.every(v => typeof v.name === 'string' && v.id &&
            (v.price == null || (typeof v.price === 'number' && Number.isFinite(v.price)))));
    }

    restoreNative() {
      if (this.config?.exclude_variants) {
        this.native = document.getElementById(this.dataset.nativeId) || this.native;
        this.syncExcludedNative();
      }
      this.native?.classList.remove('jl-picker__native');
      this.native?.removeAttribute('aria-hidden');
      this.native?.removeAttribute('inert');
      this.unlockForms();
    }

    isExcluded(variant) {
      return Boolean(variant && this.config?.exclude_variants?.ids?.includes(String(variant.id)));
    }

    allowedTuples() {
      return this.config?.exclude_variants?.allowed_tuples;
    }

    exclusionState(state) {
      if (!this.config?.exclude_variants) return state;
      const { ids, tuples, allowed_tuples: allowed } = this.config.exclude_variants;
      if (!Array.isArray(ids) || ids.some(id => typeof id !== 'string') ||
          !Array.isArray(tuples) || !Array.isArray(allowed) || !allowed.length ||
          [...tuples, ...allowed].some(row => !Array.isArray(row) || row.length !== state.options.length ||
            row.some((name, i) => typeof name !== 'string' || !state.options[i].values.some(v => v.name === name))) ||
          allowed.some(row => tuples.some(excluded => excluded.every((name, i) => name === row[i])))) {
        throw new Error('Invalid precomputed exclusions');
      }
      const picks = this.selected(state).map(v => v.name);
      const excluded = this.isExcluded(state.variant) || tuples.some(row => row.every((name, i) => name === picks[i]));
      return { ...state, variant: excluded ? null : state.variant,
        options: state.options.map((option, i) => ({ ...option, values: option.values.map(value => {
          const rows = allowed.filter(row => row[i] === value.name);
          const exists = rows.some(row => row.every((name, j) => j === i || name === picks[j]));
          return { ...value, excluded: !rows.length, exists: value.exists && exists,
            selected: value.selected && rows.length > 0,
            available: value.available && rows.some(row => picks.slice(0, i).every((name, j) => name === row[j])),
            price: exists ? value.price : null };
        }) })) };
    }

    allowedIds(ids, state = this.state) {
      const rows = this.allowedTuples();
      if (!rows) return ids;
      const names = state.options.map((o, i) => o.values.find(v => v.id === ids[i])?.name);
      const score = row => row.reduce((sum, name, i) => sum + (name === names[i] ? 1 : 0), 0);
      const row = rows.reduce((best, row) => score(row) > score(best) ? row : best);
      return row.map((name, i) => state.options[i].values.find(v => v.name === name).id);
    }

    syncExcludedNative() {
      try {
        const rows = this.allowedTuples();
        if (!rows?.length) throw new Error('Missing allowed tuples');
        const state = this.readJSON(this, '[data-jl-state]');
        const groups = [...this.native.querySelectorAll('fieldset')].map(field => [...fieldsetInputs(field)]);
        if (!groups.length) groups.push(...[...this.native.querySelectorAll('select')].map(select => [...select.querySelectorAll('option')]));
        if (groups.length !== state.options.length || groups.some((group, i) =>
            state.options[i].values.some(v => !group.some(c => c.dataset.optionValueId === v.id)))) throw new Error('Native option markup mismatch');
        this.nativeExclusionListeners?.abort();
        this.nativeExclusionListeners = new AbortController();
        // Local capture repairs a forbidden tuple before Woodstock reads it.
        this.native.addEventListener('change', event => {
          const ids = groups.map(group => group.find(c => c.checked || c.selected)?.dataset.optionValueId);
          const corrected = this.allowedIds(ids, state);
          if (ids.some((id, i) => id !== corrected[i])) {
            event.preventDefault(); event.stopImmediatePropagation();
          }
          this.syncExcludedNative();
        }, { capture: true, signal: this.nativeExclusionListeners.signal });
        const ids = groups.map(group => group.find(c => c.checked || c.selected)?.dataset.optionValueId);
        const corrected = this.allowedIds(ids, state);
        groups.forEach((group, i) => group.forEach(control => {
          const value = state.options[i].values.find(v => v.id === control.dataset.optionValueId);
          const selected = state.options.map((o, j) => o.values.find(v => v.id === corrected[j]).name);
          const excluded = !value || !rows.some(row => row[i] === value.name &&
            selected.slice(0, i).every((name, j) => name === row[j]));
          control.disabled = excluded; control.hidden = excluded;
          const label = control.closest('li') || this.native.querySelector(`label[for="${control.id}"]`);
          if (label) label.hidden = excluded;
          control[control.tagName === 'OPTION' ? 'selected' : 'checked'] = control.dataset.optionValueId === corrected[i];
        }));
        groups.forEach((group, i) => {
          const field = group[0].closest('fieldset') || group[0].closest('select');
          if (field) field.hidden = new Set(rows.map(row => row[i])).size === 1;
        });
        if (ids.some((id, i) => id !== corrected[i])) {
          const i = ids.findIndex((id, i) => id !== corrected[i]);
          const target = groups[i].find(c => c.dataset.optionValueId === corrected[i]);
          (target.tagName === 'OPTION' ? target.parentNode : target).dispatchEvent(new Event('change', { bubbles: true }));
        }
      } catch (error) {
        console.warn('JL picker: could not apply native exclusions; restoring native controls.', error);
      }
    }

    hideNative() {
      this.native.classList.add('jl-picker__native');
      this.native.setAttribute('aria-hidden', 'true');
      this.native.setAttribute('inert', '');
    }

    makeSteps() {
      const steps = this.state.options.flatMap((option, index) => index === this.splitIndex
        ? this.labels.map((name, part) => ({ name, index, part }))
        : [{ name: option.name, index, part: null }]);
      const names = steps.map(s => s.name);
      if (new Set(names).size !== names.length) throw new Error('Step names must be unique');
      const order = this.config.option_order || [];
      if (!Array.isArray(order) || !Array.isArray(this.labels)) throw new Error('Invalid step order');
      // Unlisted steps are appended so a setup can never accidentally drop an option.
      const ordered = [...order.map(name => steps.find(s => s.name === name)).filter(Boolean),
        ...steps.filter(s => !order.includes(s.name))].filter((s, i, all) => all.indexOf(s) === i)
        .concat((this.config.extras || []).map(extra => ({ ...extra, extra: true })));
      const tier = ordered.findIndex(step => step.name === 'Pricing Tier');
      if (tier >= 0 && (this.config.addons || this.treeLeaves().some(leaf => leaf.path.some(p => this.isUpgrade(p.question))))) {
        ordered.splice(tier + 1, 0, { name: 'Upgrades (optional)', upgrades: true });
      } else if (this.config.addons) {
        ordered.push({ name: 'Upgrades (optional)', upgrades: true });
      }
      return ordered;
    }

    validateExtras() {
      if (this.config.replace_addon != null && typeof this.config.replace_addon !== 'boolean') {
        throw new Error('Invalid add-on replacement flag');
      }
      const extras = this.config.extras;
      if (extras == null) return;
      if (!Array.isArray(extras)) throw new Error('Invalid extras');
      const names = this.steps.map(s => s.name);
      if (new Set(names).size !== names.length) throw new Error('Step names must be unique');
      const previous = new Set(this.steps.filter(s => !s.extra).map(s => s.name));
      extras.forEach(extra => {
        if (!extra || typeof extra.name !== 'string' || !extra.name.trim() || /[\[\]\r\n]/.test(extra.name) ||
            (extra.summary_label != null && (typeof extra.summary_label !== 'string' || !extra.summary_label.trim())) ||
            typeof extra.required !== 'boolean' || (extra.note != null && typeof extra.note !== 'string') ||
            !Array.isArray(extra.values) || !extra.values.length ||
            extra.values.some(v => typeof v !== 'string' || !v.trim()) ||
            new Set(extra.values).size !== extra.values.length) throw new Error('Invalid extra');
        if (extra.default != null && !extra.values.includes(extra.default)) throw new Error('Invalid extra default');
        if (extra.native_option != null && (typeof extra.native_option !== 'string' || !extra.native_option.trim() ||
            !extra.native_value_map || typeof extra.native_value_map !== 'object' || Array.isArray(extra.native_value_map) ||
            Object.entries(extra.native_value_map).some(([value, native]) => !extra.values.includes(value) || typeof native !== 'string' || !native))) throw new Error('Invalid native extra mapping');
        if (extra.surcharge_pct != null && (typeof extra.surcharge_pct !== 'object' || Array.isArray(extra.surcharge_pct) ||
            Object.entries(extra.surcharge_pct).some(([value, pct]) => !extra.values.includes(value) ||
              typeof pct !== 'number' || !Number.isFinite(pct) || pct <= 0 || pct > 100))) throw new Error('Invalid extra surcharge');
        if (extra.hide_when != null) {
          if (typeof extra.hide_when !== 'object' || Array.isArray(extra.hide_when)) throw new Error('Invalid extra rules');
          Object.entries(extra.hide_when).forEach(([name, rules]) => {
            if (!previous.has(name) || !rules || typeof rules !== 'object' || Array.isArray(rules) ||
                Object.values(rules).some(values => !Array.isArray(values) ||
                  values.some(value => !extra.values.includes(value)))) throw new Error('Invalid extra rules');
          });
        }
        previous.add(extra.name);
      });
    }

    currentChoice(step) {
      if (step.upgrades) return this.upgradeSummary();
      return step.extra ? this.extraSelections?.get(step.name) || '' : this.partName(this.selected()[step.index], step);
    }

    extraChoices(step) {
      const hidden = new Set(Object.entries(step.hide_when || {}).flatMap(([name, rules]) => {
        const dependency = this.steps.find(s => s.name === name);
        return rules[this.currentChoice(dependency)] || [];
      }));
      return step.values.filter(value => !hidden.has(value) && (!step.native_option || Object.prototype.hasOwnProperty.call(step.native_value_map || {}, value)) &&
        (!step.surcharge_pct?.[value] || this.surchargeOffer(step, value)));
    }

    stepHidden(step) {
      if (step.upgrades) {
        const tier = this.steps.find(s => s.name === 'Pricing Tier');
        if (tier && this.treeEnabled(tier)) this.treeView(tier);
        return Boolean(this.treePending || this.variantFallback || !this.upgradeGroups().length &&
          !this.config.addons?.groups.some(g => g.choices.some(c => this.addonOffer(c.id))));
      }
      if (this.config.exclude_variants && !step.extra && this.choices(step).length === 1) return true;
      return Boolean(step.extra ? !this.extraChoices(step).length :
        (this.config.extras || []).some(extra => extra.native_option === step.name));
    }

    reconcileExtras() {
      this.steps.filter(s => s.extra).forEach(step => {
        // Never send a previously selected value that the new shape disallows.
        const allowed = this.extraChoices(step);
        if (!allowed.includes(this.extraSelections?.get(step.name))) {
          this.extraSelections?.delete(step.name);
          // Defaults opt in to automatic choice. Preserve any still-allowed customer choice.
          const value = allowed.includes(step.default) ? step.default : step.default != null && allowed.length === 1 ? allowed[0] : null;
          if (value) this.extraSelections?.set(step.name, value);
        }
      });
    }

    syncNativeExtras(state = this.state) {
      this.steps.filter(step => step.extra && step.native_option).forEach(step => {
        const index = state.options.findIndex(option => option.name === step.native_option);
        const selected = index < 0 ? null : this.selected(state)[index];
        if (!selected) return;
        const raw = selected.name;
        const previous = this.extraSelections.get(step.name);
        // Keep an explicit special CCT (2700K/5000K) when its underlying
        // native 3K value is unchanged. Otherwise follow the selected variant.
        const retained = previous && step.native_value_map[previous] === raw ? previous : null;
        const canonical = this.config.value_labels?.[step.native_option]?.[raw] || raw;
        const mapped = retained || (step.native_value_map[canonical] === raw ? canonical :
          Object.entries(step.native_value_map).find(([, value]) => value === raw)?.[0]);
        if (mapped && this.extraChoices(step).includes(mapped)) this.extraSelections.set(step.name, mapped);
        else this.extraSelections.delete(step.name);
      });
    }

    purchaseInfos() {
      const own = this.closest('product-info');
      const path = url => new URL(url, location.origin).pathname;
      return [...document.querySelectorAll('product-info')].filter(info => info === own ||
        (info.dataset.url && path(info.dataset.url) === path(this.dataset.productUrl)));
    }

    purchaseForms() {
      const explicit = this.dataset.formId && document.getElementById(this.dataset.formId);
      return [...new Set([...(explicit ? [explicit] : []), ...this.purchaseInfos().flatMap(info =>
        [...info.querySelectorAll('product-form form, form[data-type="add-to-cart-form"]')])])]
        // Shopify's input[name="id"] shadows HTMLFormElement.id.
        .filter(form => form.getAttribute('id') && !form.getAttribute('id').includes('installment'));
    }

    purchaseOwner() {
      return [...document.querySelectorAll('jl-smart-picker')].find(picker =>
        picker.extraSelections === this.extraSelections && picker.steps && !picker.listeners?.signal.aborted) || this;
    }

    restoreAddon() {
      this.productInfos?.forEach(info => info.removeAttribute('data-jl-replace-addon'));
      this.addonInputs?.forEach((wasDisabled, input) => { input.disabled = wasDisabled; });
      this.addonInputs?.clear();
      this.productInfos?.forEach(info => info.removeAttribute('data-jl-enhanced'));
      this.paymentStates?.forEach((state, payment) => {
        payment.removeAttribute('data-jl-extras-pending');
        payment.removeAttribute('data-jl-payment');
      });
      this.purchaseButtons?.forEach((state, button) => {
        const label = button.querySelector('span') || button;
        if (label.textContent !== state.text) label.textContent = state.text;
        button.disabled = state.disabled;
        button.removeAttribute('data-jl-primary');
      });
      this.purchaseSummaries?.forEach(summary => summary.remove());
      this.purchaseSummaries?.clear();
    }

    missingStep() {
      return this.steps.find(step => !step.upgrades && !this.stepHidden(step) && (step.extra ? step.required : true) &&
        !this.choices(step).includes(this.currentChoice(step)));
    }

    quantityValid(form) {
      const input = form.querySelector('[name="quantity"]');
      if (!input) return true;
      const value = Number(input.value);
      const min = Number(input.getAttribute('min') || 1);
      const max = Number(input.getAttribute('max') || Infinity);
      const increment = Number(input.getAttribute('step') || 1);
      return Number.isInteger(value) && value >= min && value <= max &&
        (value - min) % increment === 0;
    }

    cartPending() {
      return this.purchaseForms().some(form => [...form.querySelectorAll('button[name="add"]')].some(button => button.classList.contains('loading')));
    }

    purchaseLabel(form) {
      if (this.cartPending()) return 'Adding to cart…';
      if (this.busy) return 'Updating choices…';
      if (this.resolutionFailed) return 'Try again';
      const missing = this.missingStep();
      if (missing) return `Choose ${/^[A-Z]{2}/.test(missing.name) ? missing.name : missing.name.toLowerCase()}`;
      if (!this.state.variant) return 'Choose your options';
      if (!this.state.variant.available) return 'Sold out';
      if (!this.quantityValid(form)) return 'Check quantity';
      return 'Add to cart';
    }

    formatPrice(cents) {
      if (typeof cents !== 'number' || !Number.isFinite(cents)) return '';
      return new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: this.dataset.currency || 'INR',
        minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: cents % 100 ? 2 : 0,
      }).format(cents / 100);
    }

    selectionText() {
      const values = this.steps.filter(step => !step.upgrades && (!this.stepHidden(step) ||
        this.config.exclude_variants && !step.extra && this.choices(step).length === 1 &&
        !(this.config.extras || []).some(extra => extra.native_option === step.name))).map(step => {
        const raw = this.currentChoice(step);
        if (!raw) return '';
        const label = this.displayValue(step, raw);
        if (!step.extra) return label;
        const summary = this.summaryLabel(step);
        return summary ? `${label} ${summary}` : label;
      }).filter(Boolean);
      const money = this.formatPrice(this.state.variant?.price);
      if (this.config.addons || this.hasSurcharges()) {
        const summary = this.addonSummary();
        const warranty = this.addonWarranty();
        const displayValues = values.map(value => warranty && value === this.displayValue('Pricing Tier', this.addonTier()) ? value.replace(/\(\d+yr\)/, `(${parseInt(warranty)}yr)`) : value);
        const hasTreeLedUpgrade = this.upgradeGroups().some(group => group.id === 'led-upgrade');
        const ledUpgrade = hasTreeLedUpgrade ? `LED upgrade: ${this.upgradeSummary()}` : '';
        return this.customerText(`Your selection: ${displayValues.join(' · ')}${ledUpgrade ? ` · ${ledUpgrade}` : ''}${summary ? ` · ${summary}` : ''}${warranty ? ` · ${warranty} warranty` : ''}${money ? ` · ${this.addonPriceText()}` : ''}`);
      }
      return this.controlText(`Your selection: ${values.join(' · ')}${money ? ` · ${money} each` : ''}`);
    }

    syncCheckoutGates(forms) {
      // One canonical theme Add button; payment widgets only become accessible
      // once that same form is ready. Never insert a substitute checkout box.
      this.paymentStates ||= new Map();
      this.purchaseButtons ||= new Map();
      this.purchaseSummaries ||= new Map();
      this.purchaseButtons.forEach((_state, button) => {
        if (!button.isConnected) {
          this.purchaseButtons.delete(button);
          this.purchaseSummaries.get(button)?.remove(); this.purchaseSummaries.delete(button);
        }
      });
      this.paymentStates.forEach((_state, payment) => { if (!payment.isConnected) this.paymentStates.delete(payment); });
      forms.forEach(form => {
        const label = this.purchaseLabel(form);
        const ready = label === 'Add to cart' && !this.selectedAddons().length;
        form.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout').forEach(payment => {
          if (!this.paymentStates.has(payment)) this.paymentStates.set(payment, {});
          if (!payment.hasAttribute('data-jl-payment')) payment.setAttribute('data-jl-payment', '');
          if (!ready && !payment.hasAttribute('data-jl-extras-pending')) payment.setAttribute('data-jl-extras-pending', '');
          if (ready && payment.hasAttribute('data-jl-extras-pending')) payment.removeAttribute('data-jl-extras-pending');
        });
        form.querySelectorAll('button[name="add"]').forEach(button => {
          const text = button.querySelector('span') || button;
          if (!this.purchaseButtons.has(button)) this.purchaseButtons.set(button, { text: text.textContent, disabled: button.disabled });
          if (text.textContent !== label) text.textContent = label;
          if (!button.hasAttribute('data-jl-primary')) button.setAttribute('data-jl-primary', '');
          // Missing choices must leave the action operable to open the right step.
          const disabled = this.busy || label === 'Adding to cart…' || label === 'Sold out' || (!this.state.variant && !this.missingStep());
          if (button.disabled !== disabled) button.disabled = disabled;
          let summary = this.purchaseSummaries.get(button);
          if (!summary || !summary.isConnected) {
            summary = this.el('p', 'jl-picker__selection');
            button.before(summary);
            this.purchaseSummaries.set(button, summary);
          }
          const selection = this.selectionText();
          if (summary.textContent !== selection) summary.textContent = selection;
        });
      });
    }

    syncPurchaseForms() {
      if (!this.isConnected || !this.steps) return;
      const owner = this.purchaseOwner();
      if (owner !== this) return owner.syncPurchaseForms();
      this.reconcileExtras();
      const forms = this.purchaseForms();
      this.installAddonCart(forms);
      this.syncAddonPrices();
      this.syncCheckoutGates(forms);
      this.productInfos = this.purchaseInfos();
      this.productInfos.forEach(info => {
        if (!info.hasAttribute('data-jl-enhanced')) info.setAttribute('data-jl-enhanced', '');
      });
      if (this.config.replace_addon === true || this.config.addons) {
        this.productInfos.forEach(info => {
          if (info.dataset.jlReplaceAddon !== 'true') info.dataset.jlReplaceAddon = 'true';
          info.querySelectorAll('button[name="add"]').forEach(button => {
            ['tepo-native-btn--hidden', 'tcustomizer-hide-button'].forEach(name => {
              if (button.classList.contains(name)) button.classList.remove(name);
            });
            if (button.style.getPropertyValue('display')) button.style.removeProperty('display');
            if (button.hidden) button.hidden = false;
            if (button.hasAttribute('aria-hidden')) button.removeAttribute('aria-hidden');
            if (button.getAttribute('tabindex') === '-1') button.removeAttribute('tabindex');
          });
          // Disable app fields as well as hiding them: stale app properties must
          // not compete with our choices in FormData or native checkout.
          info.querySelectorAll('form .tcustomizer, form #tcustomizer-app').forEach(block => {
            block.querySelectorAll('input, select, textarea').forEach(input => {
              this.addonInputs ||= new Map();
              if (!this.addonInputs.has(input)) this.addonInputs.set(input, input.disabled);
              if (!input.disabled) input.disabled = true;
            });
          });
        });
      }
      const entries = forms.flatMap(form => this.steps.filter(s => s.extra).flatMap(step => {
        const value = this.extraSelections?.get(step.name);
        return value ? [{ form: form.getAttribute('id'), name: `properties[${step.name}]`, value }] : [];
      }));
      const key = JSON.stringify(entries);
      if (this.propertiesKey === key && this.propertyInputs?.isConnected) return;
      this.propertyInputs ||= document.createElement('div');
      this.propertyInputs.hidden = true;
      this.propertyInputs.dataset.jlProperties = '';
      const fragment = document.createDocumentFragment();
      entries.forEach(entry => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = entry.name;
        input.value = entry.value;
        input.setAttribute('form', entry.form);
        fragment.append(input);
      });
      this.propertyInputs.replaceChildren(fragment);
      if (!this.propertyInputs.isConnected) this.append(this.propertyInputs);
      this.propertiesKey = key;
    }

    guardPurchase(event) {
      if (this.purchaseOwner() !== this) return;
      if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
      const forms = this.purchaseForms();
      const path = event.composedPath?.() || [event.target];
      const targets = path.filter(node => node?.matches?.(
        'button[name="add"], button[name="tcustomizer-add"], [type="submit"], [name="checkout"], [data-jl-extra-checkout], .shopify-payment-button, shopify-accelerated-checkout'));
      // A button inside a payment shadow root may have no form of its own;
      // continue along the composed path to the payment host's product form.
      const form = event.type === 'submit' ? event.target : targets
        .map(target => target.form || target.closest('form')).find(candidate => forms.includes(candidate));
      if (!forms.includes(form)) return;
      this.syncPurchaseForms();
      if (this.selectedAddons().length && typeof form.closest('product-form')?.prepareRequestConfig !== 'function') {
        event.preventDefault(); event.stopImmediatePropagation();
        this.say('Upgrade cart is unavailable. Please reload this page.'); return;
      }
      const lightId = form.querySelector('[name="id"]');
      if (this.isExcluded(this.state.variant) || this.config.exclude_variants?.ids?.includes(String(lightId?.value))) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      if (this.selectedAddons().length && lightId && String(lightId.value) !== String(this.state.variant?.id)) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.say('Your light is still updating. Please try again.'); return;
      }
      const missing = this.missingStep();
      const quantityInvalid = !this.quantityValid(form);
      if (this.selectedAddons().length && targets.some(t => t.matches('.shopify-payment-button, shopify-accelerated-checkout, [name="checkout"]'))) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      if (!this.busy && !this.cartPending() && !missing && !this.resolutionFailed && this.state.variant?.available && !quantityInvalid) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (missing) {
        this.missingExtra = missing.name;
        this.openStep(missing.name, true);
        this.say('');
      } else if (this.resolutionFailed) {
        this.resolutionFailed = false;
        this.selectValue(this.retryStep, this.retryValue);
      } else if (quantityInvalid) {
        const input = form.querySelector('[name="quantity"]');
        this.say(`Choose a quantity of at least ${input?.getAttribute('min') || 1}${input?.getAttribute('max') ? ` and at most ${input.getAttribute('max')}` : ''}, in steps of ${input?.getAttribute('step') || 1}.`);
        form.querySelector('[name="quantity"]')?.focus();
      } else {
        this.say(this.busy ? 'Please wait while your choices update.' : this.cartPending() ? 'Adding to cart…' : 'This selection is sold out.');
      }
    }

    customerText(value) {
      if (typeof value !== 'string') return value;
      return this.controlText(value);
    }

    controlText(value) {
      return value
        .replace(/\bRF\s*\+\s*BLE(?= (?:Dimming|Tunable|\())/g, 'Remote + app (RF + BLE)')
        .replace(/\bRF (?=Dimming|Tunable)/g, 'Remote only (RF) ')
        .replace(/((?:Remote only \(RF\)|Remote \+ app \(RF \+ BLE\)|Zigbee)) (Dimming|Tunable)/g,
          (_, control, mode) => `${control} ${mode.toLowerCase()}`);
    }

    validateAddons() {
      const c = this.config.addons;
      if (c == null) return;
      const fail = () => { throw new Error('Invalid addons configuration'); };
      const obj = v => v && typeof v === 'object' && !Array.isArray(v);
      const text = v => typeof v === 'string' && v.trim() && !/[\r\n]/.test(v);
      const exact = (v, keys) => obj(v) && Object.keys(v).sort().join('|') === keys.sort().join('|');
      if (!exact(c, ['products', 'rules', 'groups', 'included', 'warranty_overrides', 'watt_rows']) ||
          !obj(c.products) || !obj(c.rules) || Object.keys(c.products).length < 1 || Object.keys(c.products).length > 10 ||
          Object.keys(c.products).sort().join('|') !== Object.keys(c.rules).sort().join('|')) fail();
      Object.entries(c.products).forEach(([id, handle]) => {
        if (!text(id) || typeof handle !== 'string' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle) ||
            !obj(c.rules[id]) || !Object.keys(c.rules[id]).length ||
            Object.entries(c.rules[id]).some(([tier, fits]) => !text(tier) || !text(fits))) fail();
      });
      if (new Set(Object.values(c.products)).size !== Object.keys(c.products).length || !Array.isArray(c.groups) || !c.groups.length) fail();
      const groups = new Set(), choices = new Map();
      c.groups.forEach(group => {
        if (!exact(group, ['id', 'label', 'choices']) || !text(group.id) || groups.has(group.id) ||
            !text(group.label) || !Array.isArray(group.choices) || !group.choices.length) fail();
        groups.add(group.id);
        group.choices.forEach(choice => {
          if (!obj(choice) || !Object.hasOwn(c.products, choice.id) || choices.has(choice.id) || !text(choice.label) ||
              ('note' in choice && !text(choice.note)) ||
              Object.keys(choice).some(k => !['id', 'label', 'requires', 'note'].includes(k))) fail();
          choices.set(choice.id, { group: group.id, choice });
        });
      });
      if (choices.size !== Object.keys(c.products).length) fail();
      choices.forEach(({ group, choice }, id) => {
        if ('requires' in choice && !obj(choice.requires)) fail();
        Object.entries(choice.requires || {}).forEach(([tier, dependency]) => {
          if (!Object.hasOwn(c.rules[id], tier) || !choices.has(dependency) || !Object.hasOwn(c.rules[dependency], tier) ||
              choices.get(dependency).group === group || Object.keys(choices.get(dependency).choice.requires || {}).length) fail();
        });
      });
      ['included', 'warranty_overrides'].forEach(field => {
        if (!obj(c[field])) fail();
        Object.entries(c[field]).forEach(([id, tiers]) => {
          if (!Object.hasOwn(c.products, id) || !obj(tiers)) fail();
          Object.entries(tiers).forEach(([tier, value]) => {
            if (!Object.hasOwn(c.rules[id], tier)) fail();
            if (field === 'included') {
              if (!Array.isArray(value) || !value.length || value.some(v => !text(v)) || new Set(value).size !== value.length) fail();
            } else if (id !== 'cri90-tunable-led' || typeof value !== 'string' || !/^[1-9][0-9]* years?$/.test(value)) fail();
          });
        });
      });
      if (!exact(c.watt_rows, ['styles']) || !obj(c.watt_rows.styles)) fail();
      Object.entries(c.watt_rows.styles).forEach(([style, row]) => {
        if (!text(style) || !obj(row) || !Object.keys(row).length || Object.keys(row).some(k => !['row', 'heads'].includes(k)) ||
            ('row' in row && !text(row.row)) || ('heads' in row && (!Number.isInteger(row.heads) || row.heads < 1 || row.heads > 100))) fail();
      });
      const copy = c.groups.map(g => g.label + ' ' + g.choices.map(c => c.label).join(' ')).join(' ').replaceAll('Flicker-free', '');
      if (/\b(?:free)\b/i.test(copy)) fail();
      // Unpublished/missing products are represented by empty arrays and simply offer no rows.
      if (!obj(this.addonProducts)) fail();
      Object.entries(this.addonProducts).forEach(([id, variants]) => {
        if (!Array.isArray(variants)) fail();
        const pairs = new Set();
        variants.forEach(v => {
          const pair = JSON.stringify(v.options);
          if (!/^\d+$/.test(String(v.id)) || !Number.isSafeInteger(v.price) || v.price < 0 || typeof v.available !== 'boolean' ||
              !Array.isArray(v.options) || v.options.length !== (id === 'special-cct' ? 1 : 2) || v.options.some(o => !text(o)) || pairs.has(pair)) fail();
          pairs.add(pair);
        });
      });
    }

    hasSurcharges() {
      return (this.config.extras || []).some(e => Object.keys(e.surcharge_pct || {}).length);
    }

    surchargeOffer(step, value) {
      const pct = step.surcharge_pct?.[value];
      const base = this.state.variant?.price;
      if (this.isExcluded(this.state.variant) || !pct || !Number.isSafeInteger(base) || base < 0 || this.variantFallback) return null;
      // Prices are paise; one ₹10 step is 1,000 paise. Round half upward.
      const price = Math.round(base * pct / 100 / 1000) * 1000;
      if (price < 1000 || price > 500000) return null;
      const variant = this.addonProducts?.['special-cct']?.find(v => v.available && !this.isExcluded(v) &&
        v.price === price && v.options.length === 1 && v.options[0] === String(price / 100));
      return variant ? { id: 'special-cct', variant, price, heads: 1,
        label: `Special colour temperature (${value})` } : null;
    }

    async loadSurcharges() {
      if (!this.hasSurcharges() || this.addonProducts?.['special-cct']?.length) return;
      // Liquid and Ajax product JSON do not expose all 500 variants at once.
      // A dedicated section paginates the hidden product, preserving preview cookies.
      try {
        const path = `${window.Shopify?.routes?.root || '/'}products/jl-addon-special-cct`;
        let url = new URL(path, location.origin);
        url.searchParams.set('section_id', 'jl-special-cct-variants');
        const variants = [], seen = new Set(), pages = new Set();
        let total;
        while (url) {
          if (pages.has(url.href) || pages.size >= 9 || url.origin !== location.origin || url.pathname !== path) throw new Error('Invalid surcharge pagination');
          pages.add(url.href);
          const response = await fetch(url.href, { credentials: 'same-origin', signal: this.listeners.signal });
          if (!response.ok) throw new Error('Surcharge catalogue unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const page = this.readJSON(html, '[data-jl-special-cct]');
          if (page.handle !== 'jl-addon-special-cct' || !Number.isInteger(page.total) || page.total < 1 || page.total > 2048 ||
              (total != null && total !== page.total) || !Array.isArray(page.variants) || !page.variants.length) throw new Error('Invalid surcharge catalogue');
          total = page.total;
          for (const v of page.variants) {
            if (!/^\d+$/.test(String(v.id)) || seen.has(String(v.id)) || !Number.isSafeInteger(v.price) ||
                v.price < 1000 || v.price > 500000 || v.price % 1000 || typeof v.available !== 'boolean' ||
                !Array.isArray(v.options) || v.options.length !== 1 || v.options[0] !== String(v.price / 100)) throw new Error('Invalid surcharge variant');
            seen.add(String(v.id)); variants.push(v);
          }
          url = page.next ? new URL(page.next, location.origin) : null;
          if (url) url.searchParams.set('section_id', 'jl-special-cct-variants');
        }
        if (variants.length !== total) throw new Error('Incomplete surcharge catalogue');
        if (!this.isConnected || this.listeners.signal.aborted) return;
        this.addonProducts ||= {};
        specialCctVariants = variants;
        this.addonProducts['special-cct'] = variants;
        this.render(); this.syncPurchaseForms();
      } catch {
        // Fail closed: surcharge values stay hidden until a complete, priced catalogue exists.
      }
    }

    addonTier() {
      const step = this.steps.find(s => s.name === 'Pricing Tier');
      return step ? this.currentChoice(step) : '';
    }

    tierCustomerText(value) {
      const labels = {
        'Triac Tunable CRI 90 (2yr)': 'Wall dimmer tunable CRI 90',
        'RF Tunable CRI 90 (2yr)': 'Remote only tunable CRI 90',
        'RF+BLE Tunable (3yr)': 'Remote + app tunable CRI 90',
        'Tunable 1800–4000K RF+BLE (3yr)': 'Remote + app tunable CRI 90 · Amber 1800–4000K',
        'Zigbee Tunable (3yr)': 'Zigbee tunable CRI 90',
      };
      return labels[value] || value;
    }

    addonRow() {
      const styleIndex = this.state.options.findIndex(o => o.name === 'Style');
      const style = styleIndex < 0 ? '' : this.selected()[styleIndex].name;
      const watt = this.steps.find(s => s.name === 'Wattage');
      const override = this.config.addons?.watt_rows.styles[style] || this.config.addons?.watt_rows.styles['*'] || {};
      const value = watt ? this.currentChoice(watt).toLowerCase() : '';
      const multi = value.match(/^(\d+)\s*[×x]\s*(.+)$/);
      return { row: override.row || (multi ? multi[2] : value), heads: override.heads || (multi ? Number(multi[1]) : 1) };
    }

    addonChoice(id) {
      return this.config.addons?.groups.flatMap(g => g.choices).find(c => c.id === id);
    }

    addonOffer(id, checkRequires = true) {
      const config = this.config.addons;
      if (!config || !this.state.variant || this.isExcluded(this.state.variant) || this.treePending || this.variantFallback) return null;
      const tier = this.addonTier();
      if (id === 'flicker-free-driver' && this.addonSelections?.get('dimming')) return null;
      const group = config.groups.find(g => g.choices.some(c => c.id === id));
      const { draft, family } = this.upgradeContext();
      // A retained None-only path has no priced tree upgrade (e.g. Glanza).
      // Suppress add-ons only when this family actually offers the same upgrade.
      if (family.some(leaf => leaf.path.some(p => p.question.id === group?.id && p.answer.label !== 'None'))) return null;
      const fits = config.rules[id]?.[tier] || config.rules[id]?.['*'];
      if (!fits) return null;
      const { row, heads } = this.addonRow();
      const included = config.included[id]?.[tier]?.includes(row);
      const variant = this.addonProducts?.[id]?.find(v => v.available && !this.isExcluded(v) && v.price > 0 && v.options[0] === fits && v.options[1] === row);
      if (!included && !variant) return null;
      const dependency = this.addonChoice(id)?.requires?.[tier];
      if (checkRequires && dependency && !this.addonOffer(dependency, false)) return null;
      return { id, variant: included ? null : variant, price: included ? 0 : variant.price * heads, heads, dependency };
    }

    reconcileAddons() {
      if (!this.config.addons) return;
      this.addonSelections ||= new Map();
      this.config.addons.groups.forEach(group => {
        const id = this.addonSelections.get(group.id);
        if (id && !this.addonOffer(id)) this.addonSelections.delete(group.id);
      });
      // Dropping an unavailable dependency also drops the dependent choice.
      this.config.addons.groups.forEach(group => {
        const id = this.addonSelections.get(group.id);
        const dependency = id && this.addonChoice(id)?.requires?.[this.addonTier()];
        if (dependency && ![...this.addonSelections.values()].includes(dependency)) this.addonSelections.delete(group.id);
      });
    }

    selectedAddons() {
      this.reconcileAddons();
      const upgrades = (this.config.addons?.groups || []).map(g => this.addonSelections.get(g.id)).filter(Boolean)
        .map(id => this.addonOffer(id)).filter(Boolean);
      const surcharges = (this.steps || []).filter(s => s.extra && !this.stepHidden(s)).map(s =>
        this.surchargeOffer(s, this.extraSelections?.get(s.name))).filter(Boolean);
      return [...upgrades, ...surcharges];
    }

    addonPriceText() {
      const base = this.state.variant?.price;
      const offers = this.selectedAddons();
      const upgrade = offers.reduce((sum, offer) => sum + offer.price, 0);
      return upgrade ? `${this.formatPrice(base)} + ${this.formatPrice(upgrade)} upgrade = ${this.formatPrice(base + upgrade)} each` : `${this.formatPrice(base)} each`;
    }

    addonSummary() {
      return this.selectedAddons().map(o => (o.label || this.addonChoice(o.id).label)).join(' · ');
    }

    chooseAddon(button) {
      const group = this.config.addons?.groups.find(g => g.id === button.dataset.addonGroup);
      const id = button.dataset.addon;
      if (!group || (id && (!group.choices.some(c => c.id === id) || !this.addonOffer(id)))) return;
      if (id) {
        // Dimming replaces the driver, so a separate flicker-free driver cannot stack.
        if (group.id === 'dimming') this.addonSelections.delete('driver-upgrade');
        if (group.id === 'driver-upgrade') this.addonSelections.delete('dimming');
        this.addonSelections.set(group.id, id);
        const dependency = this.addonOffer(id).dependency;
        if (dependency) {
          const requiredGroup = this.config.addons.groups.find(g => g.choices.some(c => c.id === dependency));
          this.addonSelections.set(requiredGroup.id, dependency);
        }
      } else {
        this.addonSelections.delete(group.id);
      }
      this.activeStep = 'Upgrades (optional)';
      this.reconcileAddons(); this.render(); this.syncPurchaseForms();
      [...this.querySelectorAll('[data-addon-group]')].find(b => b.dataset.addonGroup === group.id && b.dataset.addon === id)?.focus();
      document.dispatchEvent(new CustomEvent('jlPickerExtrasChanged', { detail: { productKey: this.productKey, source: this } }));
    }

    renderAddons(panel) {
      if (!this.config.addons || this.treePending || this.variantFallback) return;
      const selected = this.selectedAddons();
      this.config.addons.groups.forEach(group => {
        const choices = group.choices.filter(c => this.addonOffer(c.id));
        if (!choices.length) return;
        const box = this.el('div', 'jl-picker__upgrade');
        box.append(this.el('div', 'jl-picker__question', group.label));
        const toggles = this.el('div', 'jl-picker__choices');
        toggles.setAttribute('role', 'radiogroup'); toggles.setAttribute('aria-label', group.label);
        [{ id: '', label: group.id === 'dimming' ? 'No dimming (standard)' : 'None' }, ...choices].forEach(choice => {
          const active = (this.addonSelections.get(group.id) || '') === choice.id;
          const button = this.el('button', 'jl-picker__upgrade-choice', choice.label);
          button.type = 'button'; button.dataset.addonGroup = group.id; button.dataset.addon = choice.id;
          button.setAttribute('role', 'radio'); button.setAttribute('aria-checked', String(active)); button.setAttribute('tabindex', active ? '0' : '-1');
          const offer = choice.id && this.addonOffer(choice.id);
          const required = selected.some(o => o.dependency === choice.id);
          button.append(this.el('small', '', required ? 'Included with amber' : offer?.price ? `+${this.formatPrice(offer.price)}` : 'Included'));
          if (choice.note) button.append(this.el('small', '', choice.note));
          toggles.append(button);
        });
        box.append(toggles); panel.append(box);
      });
    }

    addonWarranty() {
      const driver = this.selectedAddons().find(o => this.addonChoice(o.id)?.note?.match(/^driver warranty \d+yr$/));
      if (driver) return this.addonChoice(driver.id).note.match(/(\d+)yr/)[1] + ' years';
      return this.selectedAddons().map(o => this.config.addons?.warranty_overrides[o.id]?.[this.addonTier()]).find(Boolean);
    }

    addonCartItems(formData, groupId) {
      const offers = this.selectedAddons();
      if (!offers.length) return null;
      const quantity = Number(formData.get('quantity') || 1);
      if (!Number.isSafeInteger(quantity) || quantity < 1 || String(formData.get('id')) !== String(this.state.variant.id)) {
        throw new Error('The light selection changed. Please try again.');
      }
      const properties = {};
      for (const [name, value] of formData.entries()) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = match[1] === 'Pricing Tier' ? this.tierCustomerText(value) : value;
      }
      properties._jl_group = groupId;
      const warranty = this.addonWarranty();
      if (warranty) properties.Warranty = warranty;
      const dimming = offers.find(o => this.config.addons?.groups.find(g => g.id === 'dimming')?.choices.some(c => c.id === o.id));
      if (dimming) properties['Driver warranty'] = this.addonChoice(dimming.id).note;
      const light = { id: String(this.state.variant.id), quantity, properties };
      if (formData.get('selling_plan')) light.selling_plan = formData.get('selling_plan');
      const style = this.state.options.findIndex(o => o.name === 'Style');
      const watt = this.steps.find(s => s.name === 'Wattage');
      const forText = [this.dataset.productTitle, style >= 0 ? this.selected()[style].name : '', watt ? this.currentChoice(watt) : '', this.tierCustomerText(this.addonTier())].filter(Boolean).join(' · ');
      return [light, ...offers.filter(o => o.variant).map(o => ({ id: String(o.variant.id), quantity: quantity * o.heads,
        properties: { For: this.customerText(forText), _jl_group: groupId, _jl_heads: String(o.heads),
          ...(o.label ? { Upgrade: o.label } : {}),
          ...(this.addonChoice(o.id)?.note ? { 'Driver warranty': this.addonChoice(o.id).note } : {}) } }))];
    }

    rewriteAddonRequest(request) {
      if (!this.selectedAddons().length) return request;
      const group = crypto.randomUUID();
      const items = this.addonCartItems(request.formData, group);
      const body = new FormData();
      for (const [name, value] of request.formData.entries()) {
        if (/^items\[/.test(name)) throw new Error('Another cart integration is active. Please reload.');
        if (!['id', 'quantity', 'selling_plan'].includes(name) && !/^properties\[/.test(name)) body.append(name, value);
      }
      items.forEach((item, i) => {
        body.append(`items[${i}][id]`, item.id); body.append(`items[${i}][quantity]`, String(item.quantity));
        if (item.selling_plan) body.append(`items[${i}][selling_plan]`, item.selling_plan);
        Object.entries(item.properties).forEach(([name, value]) => body.append(`items[${i}][properties][${name}]`, value));
      });
      // Keep request.formData for the theme's variant ID/error events; only replace the wire body.
      request.config.body = body;
      return request;
    }

    installAddonCart(forms) {
      if (!this.config.addons && !this.hasSurcharges()) return;
      this.cartHooks ||= new Map();
      this.cartHooks.forEach(({ original, wrapper }, productForm) => {
        if (!productForm.isConnected) {
          if (productForm.prepareRequestConfig === wrapper) productForm.prepareRequestConfig = original;
          this.cartHooks.delete(productForm);
        }
      });
      forms.forEach(form => {
        const productForm = form.closest('product-form');
        if (!productForm || this.cartHooks.has(productForm) || typeof productForm.prepareRequestConfig !== 'function') return;
        const original = productForm.prepareRequestConfig;
        const picker = this;
        const wrapper = function (...args) {
          return picker.rewriteAddonRequest(original.apply(this, args));
        };
        this.cartHooks.set(productForm, { original, wrapper });
        productForm.prepareRequestConfig = wrapper;
      });
    }

    syncAddonPrices() {
      if (!this.config.addons && !this.hasSurcharges()) return;
      this.addonPriceNodes ||= new Map();
      this.addonPriceNodes.forEach((node, container) => {
        if (!container.isConnected || !this.selectedAddons().length) {
          node.remove(); this.addonPriceNodes.delete(container);
        }
      });
      if (!this.selectedAddons().length) return;
      const sections = new Set([this.dataset.section, ...this.purchaseInfos().map(info => info.dataset.section).filter(Boolean)]);
      sections.forEach(section => ['price', 'price-top'].forEach(prefix => {
        const container = document.getElementById(`${prefix}-${section}`);
        if (!container) return;
        let node = this.addonPriceNodes.get(container);
        if (!node || !node.isConnected) {
          node = this.el('p', 'jl-picker__addon-price'); container.append(node);
          this.addonPriceNodes.set(container, node);
        }
        const text = `Light + upgrades: ${this.addonPriceText()}`;
        if (node.textContent !== text) node.textContent = text;
      }));
    }

    restoreAddonCart() {
      this.cartHooks?.forEach(({ original, wrapper }, form) => {
        if (form.prepareRequestConfig === wrapper) form.prepareRequestConfig = original;
      });
      this.cartHooks?.clear();
      this.addonPriceNodes?.forEach(node => node.remove());
      this.addonPriceNodes?.clear();
    }

    selected(state = this.state) {
      return state.options.map(o => o.values.find(v => v.selected) || o.values[0]);
    }

    partName(value, step) {
      return step.part === null ? value.name : value.name.split(this.separator)[step.part];
    }

    eligible(value) {
      // Shopify availability is a subtree test, not a scan of product.variants.
      // A sold-out but existing selected variant stays visible and non-purchasable.
      return !value.excluded && (value.available || value.exists || value.selected);
    }

    validateValueGroups() {
      const configured = this.config.value_groups;
      if (configured == null) return; // Existing v1 setups remain ungrouped.
      if (typeof configured !== 'object' || Array.isArray(configured)) throw new Error('Invalid value groups');
      Object.values(configured).forEach(groups => {
        if (!Array.isArray(groups)) throw new Error('Invalid value groups');
        const labels = new Set(['More options']);
        const values = new Set();
        groups.forEach(group => {
          if (!group || typeof group.group !== 'string' || !group.group.trim() ||
              /[\r\n]/.test(group.group) || labels.has(group.group) ||
              typeof group.note !== 'string' || /[\r\n]/.test(group.note) ||
              !Array.isArray(group.values) || !group.values.length ||
              group.values.some(value => typeof value !== 'string' || !value.trim() || values.has(value))) {
            throw new Error('Invalid value group');
          }
          labels.add(group.group);
          group.values.forEach(value => {
            if (values.has(value)) throw new Error('Duplicate grouped value');
            values.add(value);
          });
        });
      });
    }

    choices(step) {
      if (step.upgrades) return [this.upgradeSummary()];
      if (step.extra) return this.extraChoices(step);
      const selected = this.selected()[step.index];
      let values = this.state.options[step.index].values.filter(v => this.eligible(v));
      if (step.part !== null) {
        const currentParts = selected.name.split(this.separator);
        values = values.filter(v => v.name.split(this.separator).every((p, i) =>
          i >= step.part || p === currentParts[i]));
      }
      return [...new Set(values.map(v => this.partName(v, step)))];
    }

    groups(step) {
      if (step.extra) return [];
      const configured = this.config.value_groups?.[step.name];
      if (!configured?.length) return [];
      const eligible = new Set(this.choices(step));
      const listed = new Set(configured.flatMap(group => group.values));
      const groups = configured.map(group => ({ ...group,
        values: group.values.filter(value => eligible.has(value)) }));
      groups.push({ group: 'More options', note: '',
        values: [...eligible].filter(value => !listed.has(value)) });
      return groups.filter(group => group.values.length);
    }

    activeGroup(step, groups, current) {
      this.groupViews ||= new Map();
      const previous = this.groupViews.get(step.name);
      // Browsing a group never selects a variant. A changed native selection
      // always opens its own group, including after contextual corrections.
      const group = (previous?.selected === current && groups.find(g => g.group === previous.group)) ||
        groups.find(g => g.values.includes(current)) || groups[0];
      this.groupViews.set(step.name, { group: group.group, selected: current });
      return group;
    }

    displayValue(step, value) {
      const name = typeof step === 'string' ? step : step.name;
      const labels = this.config.value_labels?.[name];
      return labels && Object.prototype.hasOwnProperty.call(labels, value) ? labels[value] : value;
    }

    summaryLabel(step) {
      if (step.summary_label) return step.summary_label;
      if (/colou?r temperature|light colou?r|\bcct\b/i.test(step.name) || /\d+\s*K\s*$/i.test(this.currentChoice(step) || '')) return '';
      return step.name.replace(/\bcolou?r\b/ig, '').replace(/\bangle\b/ig, '').trim().toLowerCase() || step.name.toLowerCase();
    }

    valueInfoKey(step, value) {
      const qualified = `${step.name} / ${value}`;
      return this.config.value_info?.[qualified] ? qualified : value;
    }

    renderGuideLinks() {
      const rows = this.querySelectorAll('.jl-picker__guide ol li');
      (this.config.guide_steps || []).forEach((step, index) => {
        if (!/^\/[A-Za-z0-9][A-Za-z0-9/_#?=&%.-]*$/.test(step.url || '')) return;
        const paragraph = rows[index]?.querySelector('p');
        if (!paragraph || rows[index].querySelector('a')) return;
        const link = this.el('a', '', 'Open the buying guide');
        link.setAttribute('href', step.url);
        // The lane companion recognises this marker and leaves the native link alone.
        link.dataset.trackGuideLink = '';
        paragraph.append(link);
      });
    }

    imageURL(reference) {
      if (!reference) return '';
      const product = this.mediaProduct || this.readMediaProduct();
      const id = value => String(value || '').split('/').pop();
      let image = reference;
      if ((typeof reference === 'object' && !reference.src && !reference.url && !reference.preview_image) || String(reference).startsWith('gid://')) {
        const variantId = reference.variant_id || (String(reference).includes('/ProductVariant/') ? reference : null);
        const mediaId = reference.media_id || reference;
        image = variantId ? product?.variants?.find(v => id(v.id) === id(variantId))?.featured_image :
          [...(product?.media || []), ...(product?.images || [])].find(m => id(m.id) === id(mediaId));
      }
      if (typeof image === 'object') image = image?.src || image?.url || image?.preview_image?.src;
      if (typeof image !== 'string' || !/^(?:(?:https:)?\/\/cdn\.shopify\.com\/|\/cdn\/shop\/)[^\s<>"']+$/.test(image)) return '';
      return image.startsWith('//') ? `https:${image}` : image;
    }

    readMediaProduct() {
      const node = this.querySelector('[data-jl-media]');
      // This optional catalogue is presentation-only. Eligibility and resolution
      // continue to use authoritative contextual option state, including >250 variants.
      if (!node) return null;
      try { this.mediaProduct = JSON.parse(node.textContent); } catch { return null; }
      return this.mediaProduct;
    }

    choiceImage(step, value) {
      const explicit = this.config.visuals?.[step.name]?.[value]?.image ||
        this.config.value_info?.[`${step.name} / ${value}`]?.image;
      if (explicit) return this.imageURL(explicit);
      if (!this.config.visuals?.auto_variant_images || step.extra) return '';
      const product = this.mediaProduct || this.readMediaProduct();
      const variant = product?.variants?.find(v => v.available &&
        (step.part === null ? v.options?.[step.index] === value :
          v.options?.[step.index]?.split(this.separator)[step.part] === value));
      return this.imageURL(variant?.featured_image);
    }

    renderChoiceImage(button, step, value) {
      // Accept art already supplied by the track companion.
      const existing = button.querySelector('img');
      const source = this.choiceImage(step, value);
      if (!source) return Boolean(existing);
      button.querySelectorAll('.jl-picker__icon, .jl-picker__swatch, [data-track-art]').forEach(node => { if (node !== existing) node.remove(); });
      button.classList.add('jl-picker__choice--photo');
      const image = existing || this.el('img');
      image.classList.add('jl-picker__photo');
      const sized = width => {
        const url = new URL(source, location.origin); url.searchParams.set('width', width); return url.href;
      };
      image.setAttribute('src', sized(240));
      image.setAttribute('srcset', `${sized(120)} 120w, ${sized(240)} 240w, ${sized(480)} 480w`);
      image.setAttribute('sizes', '(max-width: 749px) 40vw, 160px');
      image.setAttribute('width', '240'); image.setAttribute('height', '240');
      image.setAttribute('loading', 'lazy'); image.setAttribute('alt', this.displayValue(step, value));
      button.setAttribute('aria-label', this.customerText(this.displayValue(step, value)));
      // Preserve native photos when the older companion also runs.
      const companion = this.config.value_info?.[value];
      image.setAttribute('data-track-art', '');
      image.setAttribute('data-track-key', companion?.image || `drawing:${companion?.drawing || 'channel'}`);
      const label = button.querySelector('.jl-picker__value');
      if (!existing) { if (label) label.before(image); else button.append(image); }
      return true;
    }

    validatePresentation() {
      const labels = this.config.value_labels;
      if (labels != null && (typeof labels !== 'object' || Array.isArray(labels) ||
          Object.values(labels).some(values => !values || typeof values !== 'object' || Array.isArray(values) ||
            Object.values(values).some(label => typeof label !== 'string' || !label.trim())))) throw new Error('Invalid value labels');
      const walk = (node, depth = 0) => {
        if (!node || depth > 12 || typeof node.id !== 'string' || typeof node.label !== 'string' ||
            !Array.isArray(node.answers) || !node.answers.length) throw new Error('Invalid tier tree');
        const labels = new Set();
        node.answers.forEach(answer => {
          if (!answer || typeof answer.label !== 'string' || labels.has(answer.label) ||
              ('next' in answer) === ('tier' in answer) || (answer.tier && typeof answer.tier !== 'string')) throw new Error('Invalid tier answer');
          labels.add(answer.label);
          if (answer.next) walk(answer.next, depth + 1);
        });
      };
      if (this.config.tier_tree) walk(this.config.tier_tree);
      if (this.config.value_info) {
        if (typeof this.config.value_info !== 'object' || Array.isArray(this.config.value_info)) throw new Error('Invalid value info');
        Object.values(this.config.value_info).forEach(info => {
          if (!info || typeof info.title !== 'string' || typeof info.text !== 'string' ||
              (info.facts && (!Array.isArray(info.facts) || info.facts.some(fact => typeof fact !== 'string')))) throw new Error('Invalid value info');
        });
      }
    }

    el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = this.customerText(text);
      return node;
    }

    treeLeaves(question = this.config.tier_tree, path = []) {
      if (!question) return [];
      return question.answers.flatMap((answer, index) => {
        const next = [...path, { question, answer, index }];
        return answer.tier ? [{ tier: answer.tier, path: next }] : this.treeLeaves(answer.next, next);
      });
    }

    tierLeaves(step) {
      const eligible = new Set(this.choices(step));
      return this.treeLeaves().filter(leaf => eligible.has(leaf.tier));
    }

    treeEnabled(step) {
      if (step.name !== 'Pricing Tier' || !this.config.tier_tree) return false;
      const leaves = this.tierLeaves(step);
      // Keep unrecognised real tiers accessible through the existing groups.
      return this.choices(step).every(value => leaves.some(leaf => leaf.tier === value));
    }

    isUpgrade(question) { return ['led-upgrade', 'driver-upgrade', 'colour-switch', 'colour-range'].includes(question.id); }

    treeView(step) {
      const leaves = this.tierLeaves(step);
      const context = JSON.stringify([this.currentChoice(step), leaves.map(leaf => leaf.tier)]);
      if (context !== this.treeContext) {
        this.treeContext = context;
        this.treePending = false;
        this.treeDraft = leaves.find(leaf => leaf.tier === this.currentChoice(step)) || leaves[0];
        this.treeDepth = Math.max(0, this.treeDraft.path.findIndex(item => this.isUpgrade(item.question)) - 1);
        if (!this.treeDraft.path.some(item => this.isUpgrade(item.question))) this.treeDepth = this.treeDraft.path.length - 1;
        // Included single-answer questions need no separate interaction.
        while (this.treeDepth > 0) {
          const prefix = this.treeDraft.path.slice(0, this.treeDepth);
          const answers = new Set(leaves.filter(leaf => prefix.every((p, i) => leaf.path[i]?.answer === p.answer))
            .map(leaf => leaf.path[this.treeDepth]?.answer.label));
          if (answers.size > 1) break;
          this.treeDepth--;
        }
      }
      return { leaves, draft: this.treeDraft, depth: this.treeDepth };
    }

    treeSummary(step) {
      const leaf = this.tierLeaves(step).find(leaf => leaf.tier === this.currentChoice(step));
      return leaf ? leaf.path.filter(item => !this.isUpgrade(item.question)).map(item => item.answer.label).filter(label => label !== 'None').join(' · ') : this.currentChoice(step);
    }

    infoButton(key, fallback = '', display = key) {
      const button = this.el('button', 'jl-picker__info');
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '18'); svg.setAttribute('height', '18');
      svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('focusable', 'false');
      svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.7');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 10v7M12 7v1');
      svg.append(path); button.append(svg);
      button.type = 'button'; button.dataset.info = key;
      button.dataset.fallback = fallback;
      if (display !== key) button.dataset.displayLabel = display;
      button.setAttribute('aria-label', this.customerText(`What's this? ${display}`));
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-expanded', 'false');
      return button;
    }

    choice(step, stepIndex, value, current) {
      const button = this.el('button', 'jl-picker__choice');
      button.type = 'button'; button.dataset.step = stepIndex; button.dataset.value = value;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', String(value === current));
      button.setAttribute('tabindex', value === current || (!current && value === this.choices(step)[0]) ? '0' : '-1');
      const visual = this.config.visuals?.[step.name]?.[value];
      if (this.renderChoiceImage(button, step, value)) {
        // Real product photos replace optional illustrations for this value.
      } else if (visual?.icon) {
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('viewBox', '0 0 32 32'); icon.setAttribute('aria-hidden', 'true');
        icon.setAttribute('class', 'jl-picker__icon');
        const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        use.setAttribute('href', `#${this.id}-icon-${visual.icon}`); icon.append(use); button.append(icon);
      } else if (visual?.color && /^(#[a-f0-9]{6}|linear-gradient\([a-z0-9#%,. –-]+\))$/i.test(visual.color)) {
        button.classList.add('jl-picker__choice--swatch');
        const chip = this.el('span', visual.light ? 'jl-picker__swatch jl-picker__swatch--light' : 'jl-picker__swatch');
        chip.setAttribute('aria-hidden', 'true'); chip.style.setProperty('background', visual.color); button.append(chip);
      }
      const label = this.displayValue(step, value);
      const warranty = step.name === 'Pricing Tier' && label.match(/^(.*?)\s*\((\d+)\s*yr\)\s*$/i);
      button.append(this.el('span', 'jl-picker__value', warranty ? warranty[1] : label));
      if (warranty) button.append(this.el('small', '', `${warranty[2]}-year warranty`));
      if (step.name === 'Wattage' || step.name === 'Lens / beam') {
        const name = step.part === null ? value : this.selected()[step.index].name.split(this.separator)
          .map((part, index) => index === step.part ? value : part).join(this.separator);
        const price = this.formatPrice(this.state.options[step.index].values.find(v => v.name === name)?.price);
        if (price) button.append(this.el('small', '', price));
      }
      const surcharge = step.extra && this.surchargeOffer(step, value);
      if (surcharge) button.append(this.el('small', '', `+${this.formatPrice(surcharge.price)}`));
      const item = this.el('div', 'jl-picker__option'); item.append(button);
      if (step.name === 'Lens / beam' || step.name === 'Pricing Tier') item.append(this.infoButton(this.valueInfoKey(step, value), '', label));
      else if (label !== value && this.config.value_info?.[`${step.name} / ${value}`]) item.append(this.infoButton(this.valueInfoKey(step, value), '', label));
      return item;
    }

    renderTree(step, stepIndex, panel) {
      const { leaves, draft, depth } = this.treeView(step);
      const priceFor = leaf => this.state.options[step.index].values.find(v => v.name === leaf?.tier)?.price;
      // The real tier remains selected until all mandatory draft questions resolve.
      const pending = this.treePending;
      draft.path.forEach((item, questionDepth) => {
        if (this.isUpgrade(item.question) || (pending && questionDepth > depth)) return;
        const block = this.el('div', 'jl-picker__tree-q'); block.dataset.depth = questionDepth;
        const title = this.el('div', 'jl-picker__question');
        title.append(this.el('span', '', item.question.label), this.infoButton(item.question.id, item.question.note || item.question.label));
        block.append(title);
        const eligible = leaves.filter(leaf => draft.path.slice(0, questionDepth).every((p, i) => leaf.path[i]?.answer === p.answer));
        const answers = item.question.answers.filter(answer => eligible.some(leaf => leaf.path[questionDepth]?.answer === answer));
        const active = pending && questionDepth === depth;
        if (active || answers.length === 1) block.append(this.el('small', 'jl-picker__tree-hint', active ? 'Choose one' : 'Only option for this light'));
        const choices = this.el('div', 'jl-picker__choices');
        // Questions whose answers are only further questions (On/off · Dimmable) are explained
        // by the question's own (i); they stay a two-up segmented choice on phones.
        const explained = answers.some(answer => answer.tier);
        if (answers.length >= 3 || explained) choices.classList.add('jl-picker__choices--rows');
        choices.setAttribute('role', 'radiogroup'); choices.setAttribute('aria-label', item.question.label);
        answers.forEach((answer, answerIndex) => {
          const option = this.el('div', 'jl-picker__option');
          const mapped = answer.tier && this.displayValue(step, answer.tier);
          const label = mapped && mapped !== answer.tier ? mapped : answer.label;
          const warranty = item.question.id === 'warranty' && label === answer.label && answer.label.match(/^(\d+ years)/);
          const button = this.el('button', 'jl-picker__tree-answer');
          button.append(this.el('span', 'jl-picker__value', warranty ? warranty[1] : label));
          button.type = 'button'; button.dataset.label = answer.label; button.dataset.treeAnswer = item.question.answers.indexOf(answer);
          button.dataset.depth = questionDepth; button.dataset.step = stepIndex;
          const checked = !active && answer === item.answer;
          button.setAttribute('role', 'radio'); button.setAttribute('aria-checked', String(checked));
          button.setAttribute('tabindex', checked || (active && answerIndex === 0) ? '0' : '-1');
          const below = eligible.filter(leaf => leaf.path[questionDepth]?.answer === answer);
          const years = [...new Set(below.map(leaf => leaf.tier.match(/\((\d+)yr\)/)?.[1]))];
          const base = below.find(leaf => leaf.path.slice(questionDepth + 1).every(p => !this.isUpgrade(p.question) || p.answer.label === 'None')) || below[0];
          const prices = below.map(priceFor).filter(price => typeof price === 'number' && Number.isFinite(price));
          const price = warranty ? this.formatPrice(priceFor(base)) : prices.length ?
            `${below.length > 1 ? 'from ' : ''}${this.formatPrice(Math.min(...prices))}` : '';
          const small = (warranty ? [this.displayValue(step, base.tier).replace(/\s*\(\d+yr\)$/, ''), price] :
            [answer.code, price, years.length === 1 && years[0] ? `${years[0]}-year warranty` : '']).filter(Boolean).join(' · ');
          if (small) button.append(this.el('small', '', small));
          option.append(button);
          if (answer.tier) this.renderChoiceImage(button, step, answer.tier);
          if (explained) {
            const raw = base?.tier || answer.tier || item.question.id;
            const display = this.displayValue(step, raw);
            option.append(this.infoButton(this.valueInfoKey(step, raw), answer.note || item.question.note, display));
          }
          else option.classList.add('jl-picker__option--plain');
          choices.append(option);
        });
        block.append(choices); panel.append(block);
      });
    }

    upgradeContext() {
      const step = this.steps.find(s => s.name === 'Pricing Tier');
      if (!step || !this.treeEnabled(step)) return { step, family: [] };
      const { leaves, draft } = this.treeView(step);
      const start = draft.path.findIndex(p => this.isUpgrade(p.question));
      const family = start < 0 ? [] : leaves.filter(leaf => draft.path.slice(0, start).every((p, i) => leaf.path[i]?.answer === p.answer));
      return { step, draft, family };
    }

    upgradeGroups() {
      const { family } = this.upgradeContext();
      const ids = [...new Set(family.flatMap(leaf => leaf.path.filter(p => this.isUpgrade(p.question)).map(p => p.question.id)))];
      return ids.map(id => ({ id, items: [...new Map(family.flatMap(leaf => leaf.path.filter(p => p.question.id === id))
        .map(p => [p.answer.label, p])).values()] })).filter(g => g.items.length > 1 ||
          (g.id === 'led-upgrade' && g.items[0]?.answer.label === 'CRI 90'));
    }

    upgradeMatch(id, label) {
      const { family, draft } = this.upgradeContext();
      // An upgrade question the candidate leaf doesn't have at all (e.g. colour range, which only follows CRI 90) must not
      // block it, or a customer who picked CRI 90 could never go back to the Eco LED.
      return family.find(leaf => leaf.path.some(p => p.question.id === id && p.answer.label === label) &&
        draft.path.every(p => p.question.id === id || leaf.path.some(other => other.question.id === p.question.id && other.answer.label === p.answer.label) ||
          (this.isUpgrade(p.question) && !leaf.path.some(other => other.question.id === p.question.id))));
    }

    upgradeSummary() {
      const { step, family, draft } = this.upgradeContext();
      const labels = family.length ? draft.path.filter(p => this.isUpgrade(p.question) && p.answer.label !== 'None').map(p => p.question.id === 'led-upgrade' && p.answer.label === 'CRI 90' ? 'CRI 90 LED' : p.answer.label) : [];
      const variantUpgrade = labels.length > 0;
      labels.push(...this.selectedAddons().map(o => (o.label || this.addonChoice(o.id).label)));
      if (!labels.length) return /Tunable Eco/.test(draft?.tier || '') ? 'None (Eco LED included)' : 'None';
      // A sole CRI 90 LED answer is included when this family has no Eco tier.
      // Keep that canonical question without losing Amber or shared add-on deltas.
      const base = family.find(leaf => leaf.path.every(p => !this.isUpgrade(p.question) || p.answer.label === 'None' ||
        (p.question.id === 'led-upgrade' && p.answer.label === 'CRI 90' &&
          !family.some(other => other.path.some(part => part.question.id === p.question.id && part.answer.label !== p.answer.label)))));
      const price = leaf => this.state.options[step.index].values.find(v => v.name === leaf?.tier)?.price;
      const known = base && Number.isFinite(price(base)) && Number.isFinite(price(draft));
      if (variantUpgrade && !known) return labels.join(' + ');
      const gap = known ? Math.max(0, price(draft) - price(base)) : 0;
      const total = gap + this.selectedAddons().reduce((sum, o) => sum + o.price, 0);
      const selectedYears = draft?.tier?.match(/\((\d+)yr\)/)?.[1];
      const baseYears = base?.tier?.match(/\((\d+)yr\)/)?.[1];
      const warrantyText = variantUpgrade && selectedYears &&
        (selectedYears !== baseYears || /Tunable/.test(draft?.tier || '')) ? ` · ${selectedYears}-year warranty` : '';
      return `${labels.join(' + ')} · ${total > 0 ? `+${this.formatPrice(total)}` : 'Included'}${warrantyText}`;
    }

    renderUpgrades(panel) {
      const { step } = this.upgradeContext();
      const price = leaf => this.state.options[step.index].values.find(v => v.name === leaf?.tier)?.price;
      this.upgradeGroups().forEach(({ id, items }) => {
        // Hide a row whose choices all need another choice first (e.g. amber colour range on an Eco LED).
        if (!items.some(p => this.upgradeMatch(id, p.answer.label))) return;
        const selected = this.treeDraft.path.find(p => p.question.id === id);
        const box = this.el('div', 'jl-picker__upgrade');
        const heading = this.el('div', 'jl-picker__question');
        heading.append(this.el('span', '', items[0].question.label), this.infoButton(id)); box.append(heading);
        const toggles = this.el('div', 'jl-picker__choices');
        toggles.setAttribute('role', 'radiogroup'); toggles.setAttribute('aria-label', items[0].question.label);
        items.forEach(p => {
          const choiceLabel = id === 'led-upgrade' && p.answer.label === 'CRI 90' ? 'CRI 90 LED' : p.answer.label;
          const button = this.el('button', 'jl-picker__upgrade-choice', choiceLabel);
          button.type = 'button'; button.dataset.upgrade = id; button.dataset.label = p.answer.label; button.dataset.step = this.steps.indexOf(step);
          const active = p.answer.label === selected?.answer.label;
          button.setAttribute('role', 'radio'); button.setAttribute('aria-checked', String(active)); button.setAttribute('tabindex', active ? '0' : '-1');
          const match = this.upgradeMatch(id, p.answer.label);
          const included = this.upgradeMatch(id, 'None') || (items.length === 1 ? match : null);
          const delta = typeof price(match) === 'number' && typeof price(included) === 'number' ? price(match) - price(included) : null;
          const selectedYears = match?.tier?.match(/\((\d+)yr\)/)?.[1];
          const baseYears = included?.tier?.match(/\((\d+)yr\)/)?.[1];
          const warrantyText = id === 'led-upgrade' && selectedYears &&
            (selectedYears !== baseYears || /Tunable/.test(match?.tier || '')) ? ` · ${selectedYears}-year warranty` : '';
          let small = p.answer.label === 'None' ? (id === 'led-upgrade' && /Tunable Eco/.test(included?.tier || '') ? 'Eco LED included' : 'Included') : delta === null ? '' : delta > 0 ? `+${this.formatPrice(delta)}${warrantyText}` : `Included${warrantyText}`;
          button.dataset.unavailable = String(!match);
          button.disabled = !match;
          if (!match) small = 'Not with this choice';
          if (small) button.append(this.el('small', '', small));
          toggles.append(button);
        });
        box.append(toggles); panel.append(box);
      });
      this.renderAddons(panel);
    }

    renderSprite() {
      if (this.querySelector('.jl-picker__sprite')) return;
      const paths = {
        option: 'M6 6h20v20H6zM10 12h12M10 17h12M10 22h7',
        size: 'M4 10V4h6M22 4h6v6M28 22v6h-6M10 28H4v-6M5 5l8 8m14-8-8 8M5 27l8-8m14 8-8-8',
        shade: 'M10 4h12l6 17H4zM16 21v7M10 28h12',
        light: 'M16 3a9 9 0 0 0-6 16l2 4h8l2-4a9 9 0 0 0-6-16M12 27h8M14 30h4',
        remote: 'M10 2h12v28H10zM16 7v1M13 13h6M13 18h6M13 23h6',
        fan: 'M16 13c-12-16-17 5-3 4M19 16c20-1 6-18-1-4M16 19c-9 18 13 14 3-1M16 13a3 3 0 1 0 0 6 3 3 0 0 0 0-6',
        colour: 'M16 3C9 11 6 15 6 20a10 10 0 0 0 20 0c0-5-3-9-10-17M11 20c0 4 3 5 5 5',
        round: 'M16 3a13 13 0 1 0 0 26 13 13 0 0 0 0-26M16 9a7 7 0 1 0 0 14 7 7 0 0 0 0-14',
        'pro-round': 'M16 8a10 10 0 1 0 0 20 10 10 0 0 0 0-20M16 13a5 5 0 1 0 0 10 5 5 0 0 0 0-10M9 3v4m5-4v4m5-4v4m5-4v4',
        'round-large': 'M16 2a14 14 0 1 0 0 28 14 14 0 0 0 0-28M16 6a10 10 0 1 0 0 20 10 10 0 0 0 0-20M16 11a5 5 0 1 0 0 10 5 5 0 0 0 0-10',
        trimless: 'M2 9h9l2 10h6l2-10h9M8 6h16M13 22h6',
        square: 'M4 4h24v24H4zM16 9a7 7 0 1 0 0 14 7 7 0 0 0 0-14',
        double: 'M2 7h28v18H2zM10 11a5 5 0 1 0 0 10 5 5 0 0 0 0-10M22 11a5 5 0 1 0 0 10 5 5 0 0 0 0-10',
        cutout: 'M2 7h11m6 0h11M13 7v8l-4 8 9 4 5-9-4-3V7M11 20l9 4',
        surface: 'M2 4h28M9 5v18c0 6 14 6 14 0V5M9 22c2-4 12-4 14 0',
        dome: 'M7 5h18v5H7zM11 13 5 28m16-15 6 15M11 10q5 7 10 0',
        blackhole: 'M7 4h18v11H7zM11 7h10v5H11zM13 18l-3 10m9-10 3 10',
        snoot: 'M9 3h14v16H9zM9 16h14M12 21l-4 8m12-8 4 8',
        zoom: 'M7 4h18v7H7zM11 14 5 28m16-14 6 14M14 14l-2 14m6-14 2 14M10 7h12',
        fisheye: 'M6 9q10-12 20 0M6 9h20M12 14 2 25m18-11 10 11M16 14v14',
      };
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'jl-picker__sprite'); svg.setAttribute('aria-hidden', 'true');
      Object.entries(paths).forEach(([name, d]) => {
        if (['option', 'size', 'shade', 'light', 'remote', 'fan', 'colour'].includes(name) &&
            !document.getElementById(`${this.id}-explained`)?.classList?.contains?.('jl-ox') &&
            !Object.values(this.config.visuals || {}).some(values => values && typeof values === 'object' && Object.values(values).some(visual => visual.icon === name))) return;
        if (['pro-round', 'round-large'].includes(name) &&
            !Object.values(this.config.visuals?.Style || {}).some(visual => visual.icon === name)) return;
        const symbol = document.createElementNS('http://www.w3.org/2000/svg', 'symbol');
        symbol.setAttribute('id', `${this.id}-icon-${name}`); symbol.setAttribute('viewBox', '0 0 32 32');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path'); path.setAttribute('d', d); symbol.append(path); svg.append(symbol);
      });
      this.append(svg);
    }

    render() {
      this.reconcileExtras();
      this.reconcileAddons();
      const container = this.querySelector('.jl-picker__steps');
      if (!container) throw new Error('Missing steps container');
      this.renderSprite();
      this.renderGuideLinks();
      if (this.activeStep === undefined || this.steps.some(step => step.name === this.activeStep && this.stepHidden(step))) {
        this.activeStep = this.missingStep()?.name || null;
      }
      const visibleSteps = this.steps.filter(step => !this.stepHidden(step));
      this.steps.forEach((step, stepIndex) => {
        let field = [...container.querySelectorAll('.jl-picker__step')].find(node => node.dataset.name === step.name);
        if (field && !field.querySelector('.jl-picker__header')) { field.remove(); field = null; }
        if (!field) {
          field = this.el('div', 'jl-picker__step'); field.dataset.name = step.name;
          const heading = this.el('h3', 'jl-picker__heading');
          const header = this.el('button', 'jl-picker__header'); header.type = 'button'; header.dataset.openStep = step.name;
          header.id = `${this.id}-header-${stepIndex}`;
          header.setAttribute('aria-controls', `${this.id}-panel-${stepIndex}`);
          header.append(this.el('span', 'jl-picker__number', stepIndex + 1), this.el('span', 'jl-picker__name', step.name),
            this.el('span', 'jl-picker__selected'), this.el('span', 'jl-picker__change', 'Change'));
          heading.append(header, this.infoButton(step.name, step.note || this.config.step_notes?.[step.name]));
          const panel = this.el('div', 'jl-picker__panel'); panel.id = `${this.id}-panel-${stepIndex}`;
          panel.setAttribute('aria-labelledby', header.id);
          field.append(heading, panel); container.append(field);
        }
        field.hidden = this.stepHidden(step) || Boolean(this.variantFallback && !step.extra);
        const header = field.querySelector('.jl-picker__header');
        const panel = field.querySelector('.jl-picker__panel');
        const open = this.activeStep === step.name;
        header.setAttribute('aria-expanded', String(open));
        const current = this.currentChoice(step);
        const chosen = this.choices(step).includes(current);
        const display = this.displayValue(step, current);
        const tree = this.treeEnabled(step);
        if (tree) this.treeView(step);
        const number = visibleSteps.indexOf(step) + 1;
        field.querySelector('.jl-picker__number').textContent = number;
        header.setAttribute('aria-label', this.controlText(`${open ? 'Close' : chosen ? 'Change' : 'Choose'} ${step.name}${chosen ? `: ${display}` : ''}. Step ${number} of ${visibleSteps.length}`));
        const summary = field.querySelector('.jl-picker__selected');
        summary.textContent = chosen ? (display !== current ? display : tree ? this.treeSummary(step) : current) : 'Not chosen';
        summary.textContent = this.customerText(summary.textContent);
        summary.classList[chosen ? 'remove' : 'add']('jl-picker__selected--missing');
        field.querySelector('.jl-picker__change').textContent = chosen ? 'Change' : 'Choose';
        panel.hidden = !open;
        // Preserve the header and all other step DOM across selections. Rebuild
        // only a panel whose choices, tree navigation or selection have changed.
        const key = JSON.stringify([current, this.choices(step),
          tree ? [this.treeDepth, this.treeDraft?.tier, this.treePending] : null,
          step.upgrades ? this.state.options.map(o => o.values.map(v => [v.name, v.price, v.exists, v.available])) : step.extra ? this.state.variant?.price : this.state.options[step.index].values.map(v => v.price),
          this.groupViews?.get(step.name), this.missingExtra,
          ...(this.config.addons ? [[...(this.addonSelections || [])], this.addonRow()] : [])]);
        if (panel.dataset.renderKey === key) return;
        panel.dataset.renderKey = key;
        const content = document.createDocumentFragment();
        if (step.upgrades) {
          this.renderUpgrades(content);
        } else if (tree) {
          this.renderTree(step, stepIndex, content);
        } else {
          const groups = this.groups(step);
          const active = groups.length ? this.activeGroup(step, groups, current) : null;
          if (active) {
            const chips = this.el('div', 'jl-picker__groups'); chips.setAttribute('role', 'group'); chips.setAttribute('aria-label', `${step.name} groups`);
            groups.forEach(group => {
              const chip = this.el('button', 'jl-picker__group', group.group); chip.type = 'button'; chip.dataset.step = stepIndex; chip.dataset.group = group.group;
              chip.setAttribute('aria-pressed', String(group === active)); chips.append(chip);
            });
            if (active.note) chips.append(this.infoButton(active.group, active.note));
            content.append(chips);
          }
          const choices = this.el('div', 'jl-picker__choices'); choices.setAttribute('role', 'radiogroup'); choices.setAttribute('aria-label', step.name);
          (active ? active.values : this.choices(step)).forEach(value => choices.append(this.choice(step, stepIndex, value, current)));
          if (!choices.querySelector('[tabindex="0"]')) choices.querySelector('.jl-picker__choice')?.setAttribute('tabindex', '0');
          content.append(choices);
        }
        panel.removeAttribute('aria-describedby');
        panel.replaceChildren(content);
      });
      container.hidden = false;
      if (this.busy) this.querySelectorAll('[role="radio"], .jl-picker__group').forEach(button => { button.disabled = true; });
      this.syncExplained();
    }

    syncExplained() {
      // A sibling in this product_info survives panel redraws. Never select another
      // product's cards; Shopify section replacement recreates both siblings.
      const root = document.getElementById(`${this.id}-explained`);
      if (!root?.classList?.contains?.('jl-ox')) return;
      root.querySelectorAll('[data-ox-color]').forEach(swatch => {
        const color = swatch.dataset.oxColor;
        if (/^(#[a-f0-9]{6}|linear-gradient\([a-z0-9#%,. –-]+\))$/i.test(color)) swatch.style.setProperty('background', color);
      });
      root.querySelectorAll('.jl-ox-card').forEach(card => {
        const step = this.steps.find(s => s.name === card.dataset.oxStep);
        if (!step) return;
        const raw = card.dataset.oxValue;
        const label = this.displayValue(step, raw);
        if (label !== raw) {
          const name = card.querySelector('.jl-ox-name');
          if (name && name.textContent !== label) name.textContent = label;
        }
        const selected = this.currentChoice(step) === raw;
        card.setAttribute('data-selected', String(selected));
        card.setAttribute('aria-current', String(selected));
        card.hidden = step.extra && !this.extraChoices(step).includes(card.dataset.oxValue);
      });
      root.querySelectorAll('.jl-ox-group').forEach(group => {
        const step = this.steps.find(s => s.name === group.dataset.oxGroup);
        group.hidden = Boolean(step && this.stepHidden(step));
        const choice = group.querySelector('.jl-ox-choice');
        if (choice) {
          const value = step && this.currentChoice(step);
          const card = [...group.querySelectorAll('.jl-ox-card')].find(node => node.dataset.oxValue === value);
          const label = card?.querySelector('.jl-ox-name')?.textContent || (step ? this.displayValue(step, value) : value);
          choice.textContent = label ? ` · ${this.controlText(label)}` : '';
        }
      });
      const current = this.activeStep || this.missingStep()?.name || '';
      // Manual explainer browsing survives unrelated redraws. A picker step
      // change takes priority and brings its corresponding explanation forward.
      if (root.dataset.oxActiveStep !== current || !root.dataset.oxReady) {
        root.openExplainedGroup?.(current);
        root.dataset.oxActiveStep = current;
        root.dataset.oxReady = root.openExplainedGroup ? 'true' : '';
      }
    }

    activePanel() {
      return [...this.querySelectorAll('.jl-picker__step')].find(field => field.dataset.name === this.activeStep)?.querySelector('.jl-picker__panel');
    }

    openStep(name, focus = false) {
      this.activeStep = name;
      this.render();
      if (focus) {
        const field = [...this.querySelectorAll('.jl-picker__step')].find(node => node.dataset.name === name);
        const target = field?.querySelector('.jl-picker__header');
        target?.focus({ preventScroll: true });
        target?.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
      }
    }

    advance(step) {
      this.activeStep = this.missingStep()?.name || null;
      this.focusStep = this.activeStep;
      this.render();
      if (this.activeStep) this.openStep(this.activeStep, true);
      else this.purchaseForms()[0]?.querySelector('button[name="add"]')?.focus({ preventScroll: true });
      const visibleSteps = this.steps.filter(s => !this.stepHidden(s));
      const count = visibleSteps.filter(s => this.choices(s).includes(this.currentChoice(s))).length;
      this.say(this.controlText(`${step.name}: ${this.displayValue(step, this.currentChoice(step))}. ${count} of ${visibleSteps.length} choices complete.`));
    }

    handleKey(event) {
      const radio = event.target.closest('[role="radio"]');
      if (!radio || !this.contains(radio) || !['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (this.busy) return;
      const radios = [...radio.closest('[role="radiogroup"]').querySelectorAll('[role="radio"]')];
      const index = radios.indexOf(radio);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? radios.length - 1 :
        (index + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + radios.length) % radios.length;
      radios[next].focus(); this.handleClick({ target: radios[next] });
    }

    closeInfo(focus = true) {
      if (!this.infoPopover) return;
      this.infoListeners?.abort();
      this.infoLayer.remove(); this.infoLayer = null; this.infoPopover = null;
      this.syncInfoOpen();
      this.infoTrigger?.setAttribute('aria-expanded', 'false');
      if (focus && this.infoTrigger?.isConnected) this.infoTrigger.focus({ preventScroll: true });
      this.infoTrigger = null;
    }

    syncInfoOpen() {
      // Another picker instance may still own an open mobile sheet.
      const open = document.querySelectorAll('.jl-picker__popover[aria-modal="true"]').length > 0;
      document.documentElement.classList[open ? 'add' : 'remove']('jl-info-open');
    }

    positionInfo() {
      if (!this.infoPopover) return;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      this.infoPopover.setAttribute('aria-modal', String(mobile));
      this.syncInfoOpen();
      if (mobile) {
        this.infoPopover.style.removeProperty('left');
        this.infoPopover.style.removeProperty('top');
        return;
      }
      const anchor = this.infoTrigger.getBoundingClientRect();
      const box = this.infoPopover.getBoundingClientRect();
      const gap = 8, edge = 16;
      const left = anchor.right - box.width;
      const below = anchor.bottom + gap;
      const top = below + box.height <= window.innerHeight - edge ? below : anchor.top - box.height - gap;
      this.infoPopover.style.setProperty('left', `${Math.max(edge, Math.min(left, window.innerWidth - box.width - edge))}px`);
      this.infoPopover.style.setProperty('top', `${Math.max(edge, Math.min(top, window.innerHeight - box.height - edge))}px`);
    }

    showInfo(trigger) {
      this.closeInfo(false); this.infoTrigger = trigger;
      const key = trigger.dataset.info;
      const info = this.config.value_info?.[key] || { title: key, text: trigger.dataset.fallback || this.config.value_notes?.[key] || 'Choose an available option for your light.' };
      const title = trigger.dataset.displayLabel || info.title || key;
      const popover = this.el('div', 'jl-picker__popover'); popover.setAttribute('role', 'dialog');
      popover.setAttribute('aria-modal', 'false'); popover.setAttribute('aria-label', title);
      const close = this.el('button', 'jl-picker__info-close', 'Close'); close.type = 'button'; close.dataset.closeInfo = '';
      close.addEventListener('click', () => this.closeInfo());
      popover.append(close, this.el('strong', '', title), this.el('p', '', info.text));
      (info.facts || []).forEach(fact => popover.append(this.el('small', '', fact)));
      if (info.image && /^(https:\/\/|\/[^/])/.test(info.image)) {
        const image = this.el('img'); image.src = info.image; image.alt = title; image.loading = 'lazy'; popover.append(image);
      }
      if (info.url && /^(#[a-z0-9-]+|\/(?!\/)[^\s]*)$/i.test(info.url)) {
        const link = this.el('a', '', 'Read more'); link.setAttribute('href', info.url); popover.append(link);
        // Local anchors originally pointed into description explainers. The
        // setup-driven sibling now owns that help when the old block is removed.
        if (info.url.startsWith('#')) {
          const root = document.getElementById(`${this.id}-explained`);
          const card = root?.classList?.contains?.('jl-ox') && [...root.querySelectorAll('.jl-ox-card')]
            .find(node => node.dataset.oxValue === key || `${node.dataset.oxStep} / ${node.dataset.oxValue}` === key);
          if (card) {
            card.id ||= `${this.id}-explained-help-${[...root.querySelectorAll('.jl-ox-card')].indexOf(card)}`;
            link.setAttribute('href', `#${card.id}`);
            link.addEventListener('click', () => {
              const toggle = root.querySelector('.jl-ox-toggle');
              if (toggle?.getAttribute('aria-expanded') === 'false') toggle.click();
              root.openExplainedGroup?.(card.dataset.oxStep);
            });
          }
        }
        link.addEventListener('click', () => this.closeInfo(false));
      }
      const layer = this.el('div', 'jl-picker__info-layer');
      const backdrop = this.el('div', 'jl-picker__backdrop'); backdrop.setAttribute('aria-hidden', 'true');
      layer.append(backdrop, popover); document.body.append(layer);
      // HTMLElement.popover is a native string attribute; keep our node separate.
      this.infoLayer = layer; this.infoPopover = popover;
      trigger.setAttribute('aria-expanded', 'true');
      this.infoListeners = new AbortController();
      const signal = this.infoListeners.signal;
      window.addEventListener('resize', () => this.positionInfo(), { signal });
      window.addEventListener('scroll', event => {
        // Inner sheet scrolling must stay usable. Page/ancestor scroll dismisses
        // the dialog so a desktop anchor can never drift away from its icon.
        if (!(event.target instanceof Node) || !popover.contains(event.target)) this.closeInfo();
      }, { signal, capture: true });
      popover.addEventListener('keydown', event => {
        if (event.key !== 'Tab' || !window.matchMedia('(max-width: 749px)').matches) return;
        const controls = [...popover.querySelectorAll('button, a[href]')];
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && event.target === first || !event.shiftKey && event.target === last) {
          event.preventDefault(); (event.shiftKey ? last : first).focus();
        }
      }, { signal });
      this.positionInfo(); close.focus({ preventScroll: true });
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('.jl-picker__choice, .jl-picker__group, .jl-picker__tree-answer, .jl-picker__upgrade-choice').forEach(b => { b.disabled = busy || b.dataset.unavailable === 'true'; });
      if (busy && !this.lockedForms) {
        const info = this.closest('product-info');
        const infos = [...document.querySelectorAll('product-info')].filter(node =>
          node === info || (info?.dataset.url && node.dataset.url === info.dataset.url));
        this.lockedForms = infos.flatMap(node => [...node.querySelectorAll('product-form')])
          .map(node => ({ node, wasInert: node.hasAttribute('inert') }));
        this.lockedForms.forEach(({ node }) => node.setAttribute('inert', ''));
      } else if (!busy) {
        this.unlockForms();
      }
      this.syncPurchaseForms();
    }

    unlockForms() {
      this.lockedForms?.forEach(({ node, wasInert }) => {
        if (!wasInert) node.removeAttribute('inert');
      });
      this.lockedForms = null;
    }

    say(text) {
      this.querySelector('.jl-picker__status').textContent = this.customerText(text);
    }

    nearest(values, previous, step) {
      // Keep as many previous split parts as possible; ties follow catalogue order.
      if (step.part !== null) {
        const old = previous.name.split(this.separator);
        const score = v => v.name.split(this.separator).reduce((sum, p, i) => sum + (p === old[i] ? 1 : 0), 0);
        return values.reduce((best, v) => score(v) > score(best) ? v : best);
      }
      return values.find(v => v.id === previous.id) || values[0];
    }

    async requestState(ids, signal) {
      const url = new URL(this.dataset.productUrl, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('section_id', this.dataset.section);
      url.searchParams.set('option_values', ids.join(','));
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Picker request failed (${response.status})`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const picker = html.getElementById(this.id);
      if (!picker) throw new Error('Picker missing from section response');
      const state = this.readJSON(picker, '[data-jl-state]');
      if (!this.validState(state)) throw new Error('Invalid option state');
      return state;
    }

    async resolve(ids, signal) {
      // Resolve holes using option-value availability, one native option at a time.
      // Each reply is authoritative and includes variants after #250. Never use
      // /products/handle.js or product.variants: both truncate at 250.
      ids = this.allowedIds(ids);
      for (let attempt = 0; attempt <= ids.length; attempt++) {
        const state = this.exclusionState(await this.requestState(ids, signal));
        if (state.variant) return state;
        let corrected = false;
        for (let i = 0; i < state.options.length; i++) {
          const values = state.options[i].values;
          const wanted = values.find(v => v.id === ids[i]);
          if (wanted?.available) continue;
          const available = values.filter(v => v.available);
          if (!available.length) throw new Error('No available combination');
          const previousIndex = values.findIndex(v => v.id === ids[i]);
          const nearest = available.reduce((best, v) =>
            Math.abs(values.indexOf(v) - previousIndex) < Math.abs(values.indexOf(best) - previousIndex) ? v : best);
          ids[i] = nearest.id;
          ids = this.allowedIds(ids, state);
          corrected = true;
          break;
        }
        if (!corrected) break;
      }
      throw new Error('No matching variant');
    }

    async handleClick(event) {
      const addon = event.target.closest('[data-addon-group]');
      if (addon && this.contains(addon) && !this.busy && !this.cartPending()) return this.chooseAddon(addon);
      const chip = event.target.closest('.jl-picker__group');
      if (chip && this.contains(chip)) {
        if (this.busy || chip.getAttribute('aria-pressed') === 'true') return;
        const step = this.steps[Number(chip.dataset.step)];
        const groups = this.groups(step);
        if (!groups.some(group => group.group === chip.dataset.group)) return;
        const current = this.partName(this.selected()[step.index], step);
        this.groupViews ||= new Map();
        this.groupViews.set(step.name, { group: chip.dataset.group, selected: current });
        this.render();
        const fieldset = [...this.querySelectorAll('.jl-picker__step')].find(s => s.dataset.name === step.name);
        fieldset?.querySelector('.jl-picker__group[aria-pressed="true"]')?.focus({ preventScroll: true });
        return;
      }
      const info = event.target.closest('.jl-picker__info');
      if (info && this.contains(info)) return this.showInfo(info);
      if (event.target.closest('.jl-picker__info-close')) return this.closeInfo();
      const header = event.target.closest('.jl-picker__header');
      if (header && this.contains(header)) return this.openStep(this.activeStep === header.dataset.openStep ? null : header.dataset.openStep);
      const back = event.target.closest('.jl-picker__back');
      if (back && this.contains(back) && !this.busy) {
        this.treeDepth = Number(back.dataset.treeBack); this.render();
        this.activePanel()?.querySelector('.jl-picker__tree-answer')?.focus(); return;
      }
      const answer = event.target.closest('.jl-picker__tree-answer, .jl-picker__upgrade-choice');
      if (answer && this.contains(answer) && !this.busy) return this.chooseTree(answer);
      const button = event.target.closest('.jl-picker__choice');
      if (!button || !this.contains(button) || this.busy) return;
      const step = this.steps[Number(button.dataset.step)];
      if (button.getAttribute('aria-checked') === 'true') return this.advance(step);
      return this.selectValue(step, button.dataset.value);
    }

    async chooseTree(button) {
      if (button.disabled) return;
      if (!button.dataset.upgrade && button.getAttribute('aria-checked') === 'true') return;
      const step = this.steps[Number(button.dataset.step)];
      const { leaves, draft } = this.treeView(step);
      let candidates;
      if (button.dataset.upgrade) {
        const match = this.upgradeMatch(button.dataset.upgrade, button.dataset.label);
        candidates = match ? [match] : [];
      } else {
        const depth = Number(button.dataset.depth);
        candidates = leaves.filter(leaf => draft.path.slice(0, depth).every((p, i) => leaf.path[i]?.answer === p.answer) &&
          leaf.path[depth]?.index === Number(button.dataset.treeAnswer));
      }
      if (!candidates?.length) return;
      const score = leaf => leaf.path.reduce((sum, p) => sum + (draft.path.some(old => old.question.id === p.question.id && old.answer.label === p.answer.label) ? 2 : 0) +
        (p.question.default === p.answer.label ? 1 : 0), 0);
      this.treeDraft = candidates.reduce((best, leaf) => score(leaf) > score(best) ? leaf : best);
      if (!button.dataset.upgrade) {
        this.treeDepth = Number(button.dataset.depth) + 1;
        // Single-answer questions are answered automatically from real leaves.
        while (this.treeDepth < this.treeDraft.path.length && !this.isUpgrade(this.treeDraft.path[this.treeDepth].question) &&
          new Set(candidates.map(leaf => leaf.path[this.treeDepth]?.answer.label)).size === 1) this.treeDepth++;
        if (this.treeDepth < this.treeDraft.path.length && !this.isUpgrade(this.treeDraft.path[this.treeDepth].question)) {
          this.treePending = true;
          this.render();
          this.activePanel()?.querySelector(`.jl-picker__tree-answer[data-depth="${this.treeDepth}"][tabindex="0"]`)?.focus(); return;
        }
      }
      this.treePending = false;
      this.treeDepth = Math.max(0, this.treeDepth - 1);
      // Upgrades are combined (LED + driver), so an upgrade tap keeps the step open for the next one.
      if (button.dataset.upgrade) {
        if (this.treeDraft.tier === this.currentChoice(step)) return this.render();
        this.activeStep = 'Upgrades (optional)';
        this.stayOnStep = true;
      } else if (this.treeDraft.tier === this.currentChoice(step)) return this.advance(step);
      return this.selectValue(step, this.treeDraft.tier);
    }

    async selectValue(step, value) {
      if (!step || this.busy) return;
      this.retryStep = step; this.retryValue = value; this.resolutionFailed = false;
      if (step.extra) {
        if (!this.extraChoices(step).includes(value)) return;
        this.extraSelections.set(step.name, value);
        if (step.native_option) {
          const native = this.steps.find(candidate => !candidate.extra && candidate.name === step.native_option);
          if (!native) { this.extraSelections.delete(step.name); return; }
          const raw = step.native_value_map[value];
          if (this.currentChoice(native) !== raw) return this.selectValue(native, raw);
        }
        this.missingExtra = null;
        this.render(); this.syncPurchaseForms(); this.advance(step);
        document.dispatchEvent(new CustomEvent('jlPickerExtrasChanged', {
          detail: { productKey: this.productKey, source: this },
        }));
        return;
      }
      const before = this.selected();
      const candidates = this.state.options[step.index].values.filter(v =>
        this.eligible(v) && this.partName(v, step) === value &&
        (step.part === null || v.name.split(this.separator).every((p, i) =>
          i >= step.part || p === before[step.index].name.split(this.separator)[i])));
      if (!candidates.length) return;
      const picked = this.nearest(candidates, before[step.index], step);
      const proposed = before.map(v => v.id);
      proposed[step.index] = picked.id;
      this.focusStep = this.stayOnStep ? 'Upgrades (optional)' : step.name;
      this.advanceAfter = this.stayOnStep ? null : step;
      this.stayOnStep = false;
      this.setBusy(true);
      this.say('Updating your choices…');
      this.request?.abort();
      const request = this.request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 15000);
      try {
        const state = await this.resolve([...proposed], request.signal);
        if (!this.isConnected || request.signal.aborted || this.request !== request) return;
        const resolved = this.selected(state);
        this.pending = resolved.map(v => v.name);
        const changes = resolved.flatMap((v, i) => v.name === before[i].name || v.name === picked.name ? [] :
          [`${state.options[i].name}: ${v.name}`]);
        // For split options, announce only automatically changed parts.
        if (step.part !== null) {
          const oldParts = before[step.index].name.split(this.separator);
          const newParts = resolved[step.index].name.split(this.separator);
          newParts.forEach((p, i) => {
            if (i !== step.part && p !== oldParts[i]) changes.push(`${this.labels[i]}: ${p}`);
          });
        }
        this.adjustment = changes.length ? `Adjusted to a matching choice — ${changes.join('; ')}.` : '';
        this.native = document.getElementById(this.dataset.nativeId);
        const fieldsets = [...this.native.querySelectorAll('fieldset')];
        const inputs = resolved.map((value, i) => {
          const radios = [...fieldsetInputs(fieldsets[i])];
          const input = radios.find(r => r.dataset.optionValueId === value.id);
          if (!input) throw new Error('Native option value not found');
          return input;
        });
        const target = inputs[step.index];
        if (!target) throw new Error('Native change target not found');
        inputs.forEach(input => { input.checked = true; });
        // Let the existing theme own price/media/URL/form/sticky cart and events.
        this.finishTimer = setTimeout(() => this.fallback('Choices could not finish updating. Please use the options below.'), 15000);
        target.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (error) {
        if (!this.isConnected) return;
        this.pending = null;
        this.setBusy(false);
        this.resolutionFailed = true;
        this.syncPurchaseForms();
        this.say('We couldn’t update this selection. Try again.');
        this.focusChoice();
      } finally {
        clearTimeout(timeout);
      }
    }

    afterVariantChanged(event) {
      if (String(event.detail?.sectionId) !== this.dataset.section) return;
      try {
        const picker = event.detail.html?.getElementById(this.id);
        if (!picker) return this.fallback();
        const state = this.exclusionState(this.readJSON(picker, '[data-jl-state]'));
        if (!state.variant || !this.validState(state)) return this.fallback();
        const actual = this.selected(state).map(v => v.name);
        const corrected = this.pending && actual.some((v, i) => v !== this.pending[i]);
        // Woodstock replaces the variant-radios element, rather than just its
        // contents. Always use the newly inserted instance for the next click.
        this.native = document.getElementById(this.dataset.nativeId);
        if (!this.native) return this.fallback();
        this.hideNative();
        this.state = state;
        this.syncNativeExtras(state);
        this.reconcileExtras();
        this.variantFallback = false;
        this.render();
        this.syncPurchaseForms();
        clearTimeout(this.finishTimer);
        this.setBusy(false);
        this.say(corrected ? 'Adjusted to the nearest matching choices.' : this.adjustment || '');
        this.pending = null;
        this.adjustment = '';
        // Woodstock focuses the hidden real radio after its fetch promise; put
        // focus back on the visible selected choice without scrolling the page.
        const advance = this.advanceAfter; this.advanceAfter = null;
        setTimeout(() => advance ? this.advance(advance) : this.focusChoice(), 0);
      } catch {
        this.fallback();
      }
    }

    focusChoice() {
      const step = [...this.querySelectorAll('.jl-picker__step')].find(s => s.dataset.name === this.focusStep);
      const target = step?.querySelector('.jl-picker__panel')?.hidden ? step.querySelector('.jl-picker__header') :
        step?.querySelector('.jl-picker__choice[aria-checked="true"], .jl-picker__tree-answer[aria-checked="true"], .jl-picker__upgrade-choice[aria-checked="true"]');
      target?.focus({ preventScroll: true });
    }

    fallback(message) {
      clearTimeout(this.finishTimer);
      this.restoreNative();
      this.setBusy(false);
      this.variantFallback = true;
      // The original variant controls recover independently; extras and their
      // required validation must remain usable even if variant enhancement fails.
      const hasExtras = this.steps.some(step => step.extra);
      this.querySelector('.jl-picker__steps').hidden = !hasExtras;
      if (hasExtras) this.querySelectorAll('.jl-picker__step').forEach((field, index) => {
        field.hidden = !this.steps[index].extra || this.stepHidden(this.steps[index]);
      });
      this.say(message || 'Please use the options below to choose your light.');
    }
  }

  function fieldsetInputs(fieldset) {
    return fieldset?.querySelectorAll('input[type="radio"]') || [];
  }
  customElements.define('jl-smart-picker', JLSmartPicker);
})();
/* CUSTOM-END: JL smart picker */
